"use client"

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from 'react'
import type { GraphPoint } from './types'
import { devPerfMeasure } from './graphUtils'
import {
  densityFromConfidenceStops,
  type BackgroundPatternSettings,
  type ConfidenceDensityStop,
  type DitherPattern,
  type NoiseSource,
} from './backgroundPatternStore'
import {
  createTrailingActorRiskConfidenceEvaluator,
  type TrailingActorRiskCapParams,
  type TrailingActorRiskYearParams,
} from './trailingActorRiskBand'
import { useBackgroundPatternSettings } from './BackgroundPatternSettingsProvider'
import {
  parseColorToRgba,
  type DitherFieldGLLayer,
} from './ditherFieldGLRenderer'
import { EPILOGUE_HUD_FADE_OUT_MS } from './graphConstants'
import { getLabelStroke } from './renderUtils'
import { riskLevels } from './capabilityThresholds'
import { useBandHudOverlayPublisher } from './BandHudOverlayChannel'

const sanitizeIdSuffix = (rawReactId: string) =>
  rawReactId.replace(/[^a-zA-Z0-9_-]/g, '')

type ThresholdOrderMatrix = ReadonlyArray<ReadonlyArray<number>>

// Ordered-dither matrices: values are the cell turn-on order.
const BAYER_4_MATRIX: ThresholdOrderMatrix = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
]

const BAYER_8_MATRIX: ThresholdOrderMatrix = [
  [0, 32, 8, 40, 2, 34, 10, 42],
  [48, 16, 56, 24, 50, 18, 58, 26],
  [12, 44, 4, 36, 14, 46, 6, 38],
  [60, 28, 52, 20, 62, 30, 54, 22],
  [3, 35, 11, 43, 1, 33, 9, 41],
  [51, 19, 59, 27, 49, 17, 57, 25],
  [15, 47, 7, 39, 13, 45, 5, 37],
  [63, 31, 55, 23, 61, 29, 53, 21],
]

const CHECKERBOARD_MATRIX: ThresholdOrderMatrix = [
  [3, 0],
  [1, 2],
]

const HORIZONTAL_LINES_MATRIX: ThresholdOrderMatrix = [
  [2, 3],
  [0, 1],
]

const VERTICAL_LINES_MATRIX: ThresholdOrderMatrix = [
  [2, 0],
  [3, 1],
]

const DIAGONAL_LINES_MATRIX: ThresholdOrderMatrix = [
  [12, 8, 4, 0],
  [9, 5, 1, 13],
  [6, 2, 14, 10],
  [3, 15, 11, 7],
]

export const PATH_BOUNDS_PADDING = 8
// Shortest transparent→full-red ramp the top-anchored field's plot-top clamp
// may produce, measured PERPENDICULAR to the min line, in px (see
// createRedAboveLossOfControlPathRender). ~1/3 of the ~360px plot: short
// enough that a mostly-visible strip still saturates at the frame top, long
// enough that a collapsing strip fades out instead of sharpening into a
// sliver. The clamp applies this as a vertical ramp scaled by the min line's
// local steepness (√(1+m²)), so a steep boundary gets a proportionally taller
// vertical ramp and the fade stays this wide across the line instead of
// thinning into a hard diagonal edge.
const MIN_CLAMPED_GRADIENT_RAMP_PX = 120
// Ceiling on the steepness scale factor: a near-vertical data jump (a whole
// log-decade in one segment) would otherwise stretch the ramp absurdly tall.
const CLAMPED_GRADIENT_RAMP_SLOPE_FACTOR_CAP = 6
const CURVE_GRADIENT_SUBDIVISIONS_PER_SEGMENT = 24
const CURVE_GRADIENT_STOPS = 8
const MIN_VISIBLE_GRADIENT_DENSITY = 0.01
// Feather blur stdDev as a fraction of average band thickness.
const BAND_HIGHLIGHT_FEATHER_FRACTION_OF_BAND = 0.35
const GLOBE_GEOMETRY_SOURCE_SELECTOR = '[data-globe-geometry-source]'
const GLOBE_EXCLUSION_SIGNATURE_PRECISION = 4
const CAPABILITY_LINE_EXCLUSION_HALF_STROKE_PX = 2
// On-line floor for the plan-preview fan's carve: dots on a non-selected
// choice line shrink to this fraction of their radius rather than vanishing,
// so thin dots still trace each line through the field instead of a fully
// white channel. Only the main (hovered/selected) line carves all the way
// down to settings.capabilityLineDotRadiusMultiplier.
const PLAN_PREVIEW_ON_LINE_DOT_RADIUS_MULTIPLIER = 0.35

export type PatternBounds = {
  x: number
  y: number
  width: number
  height: number
}

export type RedAboveLossOfControlPathRender = {
  fillPathData: string
  bounds: PatternBounds
  minLineScreenPoints: ScreenPoint[]
  maxLineScreenPoints: ScreenPoint[]
}

type PathRender = RedAboveLossOfControlPathRender

export type ScreenPoint = {
  x: number
  y: number
}

type GlobeDotExclusionMask = {
  width: number
  height: number
  padding: number
  data: Uint8ClampedArray
}

type GlobeDotExclusionMaskSnapshot = {
  mask: GlobeDotExclusionMask | null
  signature: string
}

// One independently-weighted set of carve polylines. Groups exist so a
// fading carve (the plan-preview fan, radiusMultiplier lerped toward 1) can
// coexist with the main line's full-strength carve; a dot near both takes
// the strongest shrink.
type PolylineDotExclusionGroup = {
  segments: PolylineSegment[]
  // Widest segment's x-extent. Segments are sorted by minX; a tile can only be
  // influenced by segments whose minX >= tileMinX - maxSegmentWidthX, so this
  // lets the per-tile scan binary-search its start instead of walking from 0
  // (the dominant graph-render cost — see getClosestDistanceSquaredToPolylineSegments).
  maxSegmentWidthX: number
  padding: number
  radiusMultiplier: number
}

type PolylineDotExclusion = {
  groups: PolylineDotExclusionGroup[]
}

// Carve polylines whose dot exclusion is mid-fade: `strength` scales the
// carve depth (0 = dots untouched, 1 = the preview lines' shallow on-line
// multiplier — see PLAN_PREVIEW_ON_LINE_DOT_RADIUS_MULTIPLIER), so the dither
// channel melts in/out in step with its lines' opacity instead of popping.
// See planPreviewCarve in useExtrapolatedGraphRenderProps.
export type FadingCapabilityLinePolylines = {
  polylines: ScreenPoint[][]
  strength: number
}

type PolylineSegment = {
  start: ScreenPoint
  end: ScreenPoint
  minX: number
  maxX: number
  minY: number
  maxY: number
}

type CurveSegment = {
  start: ScreenPoint
  end: ScreenPoint
}

type CurveLookup = {
  points: ScreenPoint[]
  segments: CurveSegment[]
  isMonotonicX: boolean
  // Per-x memo of interpolateCurveLookupYAtX. The dither samples the curve at a
  // tile's x once per ROW of tiles in that column, but the answer depends only
  // on x — so the same x recurs every row. Caching collapses the binary search
  // from once-per-tile to once-per-column (~rows× fewer). A lookup is rebuilt
  // each frame from fresh screen points, so the cache is never stale.
  yAtXCache: Map<number, number | null>
}


type RenderIds = {
  gradientLuminanceId: string
  gradientSurfaceId: string
  maskId: string
  continuousNoiseFilterId: string
  bandHighlightFeatherFilterId: string
  bandHighlightFeatherMaskId: string
}

const clamp01 = (value: number): number =>
  Math.max(0, Math.min(1, value))

const grayLevelFill = (level: number): string => {
  const clamped = clamp01(level)
  const channel = Math.round(clamped * 255)
  return `rgb(${channel}, ${channel}, ${channel})`
}

type RgbColor = { r: number; g: number; b: number }
type HslColor = { h: number; s: number; l: number }

const hexToRgb = (hexColor: string): RgbColor | null => {
  const matches = hexColor.match(/^#?([0-9a-fA-F]{6})$/)
  if (!matches) return null
  const numericValue = parseInt(matches[1], 16)
  return {
    r: (numericValue >> 16) & 0xff,
    g: (numericValue >> 8) & 0xff,
    b: numericValue & 0xff,
  }
}

const rgbToHsl = ({ r, g, b }: RgbColor): HslColor => {
  const rNormalized = r / 255
  const gNormalized = g / 255
  const bNormalized = b / 255
  const maxChannel = Math.max(rNormalized, gNormalized, bNormalized)
  const minChannel = Math.min(rNormalized, gNormalized, bNormalized)
  const lightness = (maxChannel + minChannel) / 2
  if (maxChannel === minChannel) return { h: 0, s: 0, l: lightness }
  const channelRange = maxChannel - minChannel
  const saturation = lightness > 0.5
    ? channelRange / (2 - maxChannel - minChannel)
    : channelRange / (maxChannel + minChannel)
  let hueSixths: number
  if (maxChannel === rNormalized) {
    hueSixths = (gNormalized - bNormalized) / channelRange
      + (gNormalized < bNormalized ? 6 : 0)
  } else if (maxChannel === gNormalized) {
    hueSixths = (bNormalized - rNormalized) / channelRange + 2
  } else {
    hueSixths = (rNormalized - gNormalized) / channelRange + 4
  }
  return { h: hueSixths / 6, s: saturation, l: lightness }
}

const hslToRgb = ({ h, s, l }: HslColor): RgbColor => {
  if (s === 0) {
    const channelValue = Math.round(l * 255)
    return { r: channelValue, g: channelValue, b: channelValue }
  }
  const upperBound = l < 0.5 ? l * (1 + s) : l + s - l * s
  const lowerBound = 2 * l - upperBound
  const hueOffsetToChannel = (hueOffset: number): number => {
    let normalizedHueOffset = hueOffset
    if (normalizedHueOffset < 0) normalizedHueOffset += 1
    if (normalizedHueOffset > 1) normalizedHueOffset -= 1
    if (normalizedHueOffset < 1 / 6) {
      return lowerBound + (upperBound - lowerBound) * 6 * normalizedHueOffset
    }
    if (normalizedHueOffset < 1 / 2) return upperBound
    if (normalizedHueOffset < 2 / 3) {
      return lowerBound
        + (upperBound - lowerBound) * (2 / 3 - normalizedHueOffset) * 6
    }
    return lowerBound
  }
  return {
    r: Math.round(hueOffsetToChannel(h + 1 / 3) * 255),
    g: Math.round(hueOffsetToChannel(h) * 255),
    b: Math.round(hueOffsetToChannel(h - 1 / 3) * 255),
  }
}

const rgbToHex = ({ r, g, b }: RgbColor): string => {
  const toHexByte = (channel: number) =>
    Math.max(0, Math.min(255, channel)).toString(16).padStart(2, '0')
  return `#${toHexByte(r)}${toHexByte(g)}${toHexByte(b)}`
}

// Scales HSL lightness/saturation by multipliers; hue preserved.
const adjustHexColorHsl = (
  hexColor: string,
  brightnessMultiplier: number,
  saturationMultiplier: number,
): string => {
  const rgb = hexToRgb(hexColor)
  if (!rgb) return hexColor
  const { h, s, l } = rgbToHsl(rgb)
  return rgbToHex(hslToRgb({
    h,
    s: Math.max(0, Math.min(1, s * saturationMultiplier)),
    l: Math.max(0, Math.min(1, l * brightnessMultiplier)),
  }))
}

// Below this, treat multiplier as 1; else geometric formula divides by ~0.
const UNIFORM_MULTIPLIER_EPSILON = 1e-9

// Recheck length so a stale override from a different densityBands can't slip in.
const isOverridesValidForBands = (
  overrides: number[] | undefined,
  bands: number,
): overrides is number[] => {
  if (!overrides) return false
  if (overrides.length !== bands + 1) return false
  return true
}

// Lower edge of band k; geometric thickness t_k = (m^k−1)/(m^N−1).
const getBandLowerProgress = (
  bandIndex: number,
  bands: number,
  multiplier: number,
  overrides?: number[]
): number => {
  if (bands < 2) return 0
  if (isOverridesValidForBands(overrides, bands)) {
    const i = Math.max(0, Math.min(bands, bandIndex))
    return overrides[i]
  }
  if (Math.abs(multiplier - 1) < UNIFORM_MULTIPLIER_EPSILON) {
    return bandIndex / bands
  }
  const totalDenominator = Math.pow(multiplier, bands) - 1
  return (Math.pow(multiplier, bandIndex) - 1) / totalDenominator
}

// Inverse of getBandLowerProgress: which band does this progress fall into?
const getBandIndexFromProgress = (
  progress: number,
  bands: number,
  multiplier: number,
  overrides?: number[]
): number => {
  if (bands < 2) return 0
  const lastBandIndex = bands - 1
  const clampedProgress = clamp01(progress)
  if (isOverridesValidForBands(overrides, bands)) {
    for (let k = bands - 1; k >= 0; k--) {
      if (clampedProgress >= overrides[k]) {
        return Math.min(lastBandIndex, k)
      }
    }
    return 0
  }
  if (Math.abs(multiplier - 1) < UNIFORM_MULTIPLIER_EPSILON) {
    return Math.min(lastBandIndex, Math.floor(clampedProgress * bands))
  }
  // For 0 < m < 1 the two negatives cancel; log argument stays positive.
  const innerArgument =
    clampedProgress * (Math.pow(multiplier, bands) - 1) + 1
  const rawIndex = Math.log(innerArgument) / Math.log(multiplier)
  return Math.min(lastBandIndex, Math.max(0, Math.floor(rawIndex)))
}

// Quantize value into bands; returns bandIndex/(bands-1) in [0,1].
const quantizeToBands = (
  value: number,
  bands: number,
  multiplier: number,
  overrides?: number[]
): number => {
  if (bands < 2) return value
  const lastBandIndex = bands - 1
  const bandIndex = getBandIndexFromProgress(value, bands, multiplier, overrides)
  return bandIndex / lastBandIndex
}

const getGradientGrayAtProgress = ({
  progress,
  gradientStrength,
  gradientCurve,
  ditherDensity,
  minDitherDensity,
  maxDitherDensity,
  densityBands,
  bandThicknessMultiplier,
  bandLowerProgressOverrides,
}: {
  progress: number
  gradientStrength: number
  gradientCurve: number
  ditherDensity: number
  minDitherDensity: number
  maxDitherDensity: number
  densityBands: number
  bandThicknessMultiplier: number
  bandLowerProgressOverrides?: number[]
}): number => {
  const topGray = Math.min(clamp01(maxDitherDensity), ditherDensity)
  const bottomGray = Math.min(
    topGray,
    Math.max(
      Math.min(minDitherDensity, topGray),
      (1 - gradientStrength) * topGray
    )
  )
  // Raw progress: 0 at LOC curve, 1 at strip top.
  const bandedProgress = quantizeToBands(
    clamp01(progress),
    densityBands,
    bandThicknessMultiplier,
    bandLowerProgressOverrides
  )
  const curvedProgress = Math.pow(bandedProgress, gradientCurve)
  return clamp01(bottomGray + ((topGray - bottomGray) * curvedProgress))
}

const clampDitherDensity = (
  density: number,
  settings: BackgroundPatternSettings
): number => {
  const maxDensity = clamp01(settings.maxDitherDensity)
  const minDensity = Math.min(maxDensity, clamp01(settings.minDitherDensity))
  return Math.min(maxDensity, Math.max(minDensity, clamp01(density)))
}

const getOrderMatrixForDitherPattern = (
  ditherPattern: DitherPattern
): ThresholdOrderMatrix | null => {
  switch (ditherPattern) {
    case 'checkerboard':
      return CHECKERBOARD_MATRIX
    case 'bayer4':
      return BAYER_4_MATRIX
    case 'bayer8':
      return BAYER_8_MATRIX
    case 'horizontalLines':
      return HORIZONTAL_LINES_MATRIX
    case 'verticalLines':
      return VERTICAL_LINES_MATRIX
    case 'diagonalLines':
      return DIAGONAL_LINES_MATRIX
    default:
      return null
  }
}

const getSvgMousePoint = (
  event: MouseEvent<SVGPathElement>
): ScreenPoint | null => {
  const svg = event.currentTarget.ownerSVGElement
  const screenMatrix = svg?.getScreenCTM()
  if (!svg || !screenMatrix) return null
  const svgPoint = svg.createSVGPoint()
  svgPoint.x = event.clientX
  svgPoint.y = event.clientY
  const localPoint = svgPoint.matrixTransform(screenMatrix.inverse())
  return { x: localPoint.x, y: localPoint.y }
}

const interpolateCurveYAtX = (
  x: number,
  curveScreenPoints: ScreenPoint[]
): number | null => {
  if (curveScreenPoints.length === 0) return null
  if (curveScreenPoints.length === 1) return curveScreenPoints[0].y
  const firstPoint = curveScreenPoints[0]
  const lastPoint = curveScreenPoints[curveScreenPoints.length - 1]
  if (x <= firstPoint.x) return firstPoint.y
  if (x >= lastPoint.x) return lastPoint.y

  for (
    let segmentIndex = 0;
    segmentIndex < curveScreenPoints.length - 1;
    segmentIndex += 1
  ) {
    const startPoint = curveScreenPoints[segmentIndex]
    const endPoint = curveScreenPoints[segmentIndex + 1]
    const minX = Math.min(startPoint.x, endPoint.x)
    const maxX = Math.max(startPoint.x, endPoint.x)
    if (x < minX || x > maxX) continue
    const segmentWidth = endPoint.x - startPoint.x
    if (segmentWidth === 0) return startPoint.y
    const segmentProgress = (x - startPoint.x) / segmentWidth
    return startPoint.y + ((endPoint.y - startPoint.y) * segmentProgress)
  }

  return null
}

const createCurveLookup = (curveScreenPoints: ScreenPoint[]): CurveLookup => {
  let isMonotonicX = true
  const segments: CurveSegment[] = []
  for (
    let segmentIndex = 0;
    segmentIndex < curveScreenPoints.length - 1;
    segmentIndex += 1
  ) {
    const start = curveScreenPoints[segmentIndex]
    const end = curveScreenPoints[segmentIndex + 1]
    if (end.x < start.x) isMonotonicX = false
    segments.push({ start, end })
  }
  return { points: curveScreenPoints, segments, isMonotonicX, yAtXCache: new Map() }
}

const interpolateSegmentYAtX = (
  x: number,
  startPoint: ScreenPoint,
  endPoint: ScreenPoint
): number => {
  const segmentWidth = endPoint.x - startPoint.x
  if (segmentWidth === 0) return startPoint.y
  const segmentProgress = (x - startPoint.x) / segmentWidth
  return startPoint.y + ((endPoint.y - startPoint.y) * segmentProgress)
}

const computeCurveLookupYAtX = (
  x: number,
  lookup: CurveLookup
): number | null => {
  const curveScreenPoints = lookup.points
  if (curveScreenPoints.length === 0) return null
  if (curveScreenPoints.length === 1) return curveScreenPoints[0].y
  if (!lookup.isMonotonicX) return interpolateCurveYAtX(x, curveScreenPoints)

  const firstPoint = curveScreenPoints[0]
  const lastPoint = curveScreenPoints[curveScreenPoints.length - 1]
  if (x <= firstPoint.x) return firstPoint.y
  if (x >= lastPoint.x) return lastPoint.y

  let low = 0
  let high = lookup.segments.length - 1
  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const segment = lookup.segments[mid]
    if (x < segment.start.x) {
      high = mid - 1
      continue
    }
    if (x > segment.end.x) {
      low = mid + 1
      continue
    }
    return interpolateSegmentYAtX(x, segment.start, segment.end)
  }

  return null
}

// Memoized by x (see CurveLookup.yAtXCache): the dither samples each column's x
// once per tile-row, so caching turns per-tile binary searches into per-column.
const interpolateCurveLookupYAtX = (
  x: number,
  lookup: CurveLookup
): number | null => {
  const cached = lookup.yAtXCache.get(x)
  if (cached !== undefined) return cached
  const value = computeCurveLookupYAtX(x, lookup)
  lookup.yAtXCache.set(x, value)
  return value
}

// Field grow/fade direction: 'top' = red-above; 'bottom' = floor-anchored.
export type DitherAnchor = 'top' | 'bottom'

export const getRawProgressAtPoint = (
  point: ScreenPoint,
  minLineScreenPoints: ScreenPoint[],
  maxLineScreenPoints: ScreenPoint[],
  anchor: DitherAnchor = 'top'
): number | null => {
  const minY = interpolateCurveYAtX(point.x, minLineScreenPoints)
  if (minY === null) return null
  const maxY = interpolateCurveYAtX(point.x, maxLineScreenPoints)
  if (maxY === null) return null
  if (anchor === 'bottom') {
    // minLine = upper edge (smaller y, progress 0); maxLine = lower (progress 1).
    if (point.y < minY) return null
    const stripHeight = maxY - minY
    if (stripHeight <= 0) return 1
    return clamp01((point.y - minY) / stripHeight)
  }
  if (point.y > minY) return null
  const stripHeight = minY - maxY
  if (stripHeight <= 0) return 1
  return clamp01((minY - point.y) / stripHeight)
}

// Cached variant of getRawProgressAtPoint for per-tile evaluation: binary
// search + per-x memoization instead of a linear scan over the raw polylines.
// The returned closure is only valid for the given line geometry — recreate it
// whenever the polylines change (the caller's memo on pathRender does this).
export const createRawProgressAtPointLookup = (
  minLineScreenPoints: ScreenPoint[],
  maxLineScreenPoints: ScreenPoint[],
  anchor: DitherAnchor = 'top'
): ((point: ScreenPoint) => number | null) => {
  const minLineLookup = createCurveLookup(minLineScreenPoints)
  const maxLineLookup = createCurveLookup(maxLineScreenPoints)
  return (point) =>
    getRawProgressAtPointFromLookups(point, minLineLookup, maxLineLookup, anchor)
}

const getRawProgressAtPointFromLookups = (
  point: ScreenPoint,
  minLineLookup: CurveLookup,
  maxLineLookup: CurveLookup,
  anchor: DitherAnchor = 'top'
): number | null => {
  const minY = interpolateCurveLookupYAtX(point.x, minLineLookup)
  if (minY === null) return null
  const maxY = interpolateCurveLookupYAtX(point.x, maxLineLookup)
  if (maxY === null) return null
  if (anchor === 'bottom') {
    if (point.y < minY) return null
    const stripHeight = maxY - minY
    if (stripHeight <= 0) return 1
    return clamp01((point.y - minY) / stripHeight)
  }
  if (point.y > minY) return null
  const stripHeight = minY - maxY
  if (stripHeight <= 0) return 1
  return clamp01((minY - point.y) / stripHeight)
}

export const getCurveGradientOpacityAtPoint = (
  point: ScreenPoint,
  minLineScreenPoints: ScreenPoint[],
  maxLineScreenPoints: ScreenPoint[],
  settings: BackgroundPatternSettings,
  anchor: DitherAnchor = 'top'
): number | null => {
  const minY = interpolateCurveYAtX(point.x, minLineScreenPoints)
  if (minY === null) return null
  if (anchor === 'bottom') {
    if (point.y < minY) return 0
  } else if (point.y > minY) {
    return 0
  }
  const progress = getRawProgressAtPoint(
    point, minLineScreenPoints, maxLineScreenPoints, anchor
  )
  if (progress === null) return 0
  const noisyProgress = clamp01(
    progress + sampleBandBoundaryProgressOffset(point, settings)
  )
  return getGradientGrayAtProgress({
    progress: noisyProgress,
    gradientStrength: settings.gradientStrength,
    gradientCurve: settings.gradientCurve,
    ditherDensity: settings.ditherDensity,
    minDitherDensity: settings.minDitherDensity,
    maxDitherDensity: settings.maxDitherDensity,
    densityBands: settings.densityBands,
    bandThicknessMultiplier: settings.bandThicknessMultiplier,
    bandLowerProgressOverrides: settings.bandLowerProgressOverrides,
  })
}

const getCurveGradientOpacityAtPointFromLookups = (
  point: ScreenPoint,
  minLineLookup: CurveLookup,
  maxLineLookup: CurveLookup,
  settings: BackgroundPatternSettings,
  anchor: DitherAnchor = 'top'
): number => {
  const minY = interpolateCurveLookupYAtX(point.x, minLineLookup)
  if (minY === null) return 0
  if (anchor === 'bottom') {
    if (point.y < minY) return 0
  } else if (point.y > minY) {
    return 0
  }
  const progress = getRawProgressAtPointFromLookups(
    point, minLineLookup, maxLineLookup, anchor
  )
  if (progress === null) return 0
  const noisyProgress = clamp01(
    progress + sampleBandBoundaryProgressOffset(point, settings)
  )
  return getGradientGrayAtProgress({
    progress: noisyProgress,
    gradientStrength: settings.gradientStrength,
    gradientCurve: settings.gradientCurve,
    ditherDensity: settings.ditherDensity,
    minDitherDensity: settings.minDitherDensity,
    maxDitherDensity: settings.maxDitherDensity,
    densityBands: settings.densityBands,
    bandThicknessMultiplier: settings.bandThicknessMultiplier,
    bandLowerProgressOverrides: settings.bandLowerProgressOverrides,
  })
}

// Band index containing the point, or null if outside / unbanded.
const getHoveredBandIndex = (
  point: ScreenPoint,
  minLineScreenPoints: ScreenPoint[],
  maxLineScreenPoints: ScreenPoint[],
  settings: BackgroundPatternSettings,
  anchor: DitherAnchor = 'top'
): number | null => {
  if (settings.densityBands < 2) return null
  const progress = getRawProgressAtPoint(
    point, minLineScreenPoints, maxLineScreenPoints, anchor
  )
  if (progress === null) return null
  const noisyProgress = clamp01(
    progress + sampleBandBoundaryProgressOffset(point, settings)
  )
  return getBandIndexFromProgress(
    noisyProgress,
    settings.densityBands,
    settings.bandThicknessMultiplier,
    settings.bandLowerProgressOverrides
  )
}

// Highest riskLevel whose value <= computeValue; '' if none. Risk is its
// own ladder (capabilityThresholds.tsx riskLevels), separate from the
// capability milestones: it has an extra 100x step with no milestone.
export const getRiskLabelForComputeValue = (computeValue: number): string => {
  let matched = ''
  for (const level of riskLevels) {
    if (level.value > computeValue) break
    matched = level.riskLabel
  }
  return matched
}

type BandHighlightRender = {
  pathData: string
  leftEdgeCenter: ScreenPoint
}

// Polygon path for band k, plus its left-edge midpoint for a label.
const buildBandHighlightRender = ({
  bandIndex,
  settings,
  minLineScreenPoints,
  maxLineScreenPoints,
}: {
  bandIndex: number
  settings: BackgroundPatternSettings
  minLineScreenPoints: ScreenPoint[]
  maxLineScreenPoints: ScreenPoint[]
}): BandHighlightRender | null => {
  const bands = settings.densityBands
  if (bands < 2) return null
  if (minLineScreenPoints.length < 2) return null
  if (maxLineScreenPoints.length === 0) return null

  const bandLowerRaw = getBandLowerProgress(
    bandIndex, bands, settings.bandThicknessMultiplier, settings.bandLowerProgressOverrides
  )
  const bandUpperRaw = getBandLowerProgress(
    bandIndex + 1, bands, settings.bandThicknessMultiplier, settings.bandLowerProgressOverrides
  )

  // One Newton-style pass solving level=rawProgress(y)+offset(x,y); exact when
  // noise wavelength in y >> band screen height. Degenerate strips collapse to Max.
  const yForLevel = (
    x: number,
    minYAtX: number,
    level: number
  ): number => {
    const maxYAtX = interpolateCurveYAtX(x, maxLineScreenPoints)
    if (maxYAtX === null) return minYAtX
    const stripHeight = minYAtX - maxYAtX
    if (stripHeight <= 0) return maxYAtX
    const unperturbedY = minYAtX - level * stripHeight
    const boundaryOffset = sampleBandBoundaryProgressOffset(
      { x, y: unperturbedY },
      settings
    )
    const adjustedLevel = clamp01(level - boundaryOffset)
    return minYAtX - adjustedLevel * stripHeight
  }

  // 1px x-sampling so polygon edges follow the noisy band borders.
  const startX = minLineScreenPoints[0].x
  const endX = minLineScreenPoints[minLineScreenPoints.length - 1].x
  const sampleCount = Math.max(1, Math.ceil(endX - startX))
  const upperEdgePoints: ScreenPoint[] = []
  const lowerEdgePoints: ScreenPoint[] = []
  for (let sampleIndex = 0; sampleIndex <= sampleCount; sampleIndex += 1) {
    const x = startX + ((endX - startX) * sampleIndex) / sampleCount
    const minYAtX = interpolateCurveYAtX(x, minLineScreenPoints)
    if (minYAtX === null) continue
    upperEdgePoints.push({ x, y: yForLevel(x, minYAtX, bandUpperRaw) })
    lowerEdgePoints.push({ x, y: yForLevel(x, minYAtX, bandLowerRaw) })
  }
  if (upperEdgePoints.length === 0) return null

  const reversedLowerEdgePoints = lowerEdgePoints.slice().reverse()
  const pathSegments: string[] = []
  upperEdgePoints.forEach((edgePoint, edgePointIndex) => {
    pathSegments.push(
      `${edgePointIndex === 0 ? 'M' : 'L'} ${edgePoint.x} ${edgePoint.y}`
    )
  })
  reversedLowerEdgePoints.forEach((edgePoint) => {
    pathSegments.push(`L ${edgePoint.x} ${edgePoint.y}`)
  })
  pathSegments.push('Z')

  const leftEdgeUpper = upperEdgePoints[0]
  const leftEdgeLower = lowerEdgePoints[0]
  return {
    pathData: pathSegments.join(' '),
    leftEdgeCenter: {
      x: leftEdgeUpper.x,
      y: (leftEdgeUpper.y + leftEdgeLower.y) / 2,
    },
  }
}

// Per-subsegment vertical gradients, Min curve up to chart top (y=0).
// Inverted strips (Min above Max — Plan A 2036+) saturate fully.
const renderGradientLuminanceDefs = ({
  topY,
  settings,
  gradientIdPrefix,
  surfaceId,
  minLineScreenPoints,
  maxLineScreenPoints,
}: {
  topY: number
  settings: BackgroundPatternSettings
  gradientIdPrefix: string
  surfaceId: string
  minLineScreenPoints: ScreenPoint[]
  maxLineScreenPoints: ScreenPoint[]
}): ReactNode => {
  const gradientDefs: ReactNode[] = []
  const bandPaths: ReactNode[] = []
  const gradientStopIndexes = Array.from(
    { length: CURVE_GRADIENT_STOPS + 1 },
    (_, stopIndex) => stopIndex
  )

  for (
    let segmentIndex = 0;
    segmentIndex < minLineScreenPoints.length - 1;
    segmentIndex += 1
  ) {
    const startPoint = minLineScreenPoints[segmentIndex]
    const endPoint = minLineScreenPoints[segmentIndex + 1]
    for (
      let subsegmentIndex = 0;
      subsegmentIndex < CURVE_GRADIENT_SUBDIVISIONS_PER_SEGMENT;
      subsegmentIndex += 1
    ) {
      const startProgress =
        subsegmentIndex / CURVE_GRADIENT_SUBDIVISIONS_PER_SEGMENT
      const endProgress =
        (subsegmentIndex + 1) / CURVE_GRADIENT_SUBDIVISIONS_PER_SEGMENT
      const startX =
        startPoint.x + ((endPoint.x - startPoint.x) * startProgress)
      const startY =
        startPoint.y + ((endPoint.y - startPoint.y) * startProgress)
      const endX =
        startPoint.x + ((endPoint.x - startPoint.x) * endProgress)
      const endY =
        startPoint.y + ((endPoint.y - startPoint.y) * endProgress)
      const midpointX = (startX + endX) / 2
      const midpointY = (startY + endY) / 2
      const maxYAtMidpoint = interpolateCurveYAtX(midpointX, maxLineScreenPoints) ?? topY
      const stripHeight = midpointY - maxYAtMidpoint
      const gradientLength = midpointY - topY
      const gradientId = `${gradientIdPrefix}-${segmentIndex}-${subsegmentIndex}`

      gradientDefs.push(
        <linearGradient
          key={`gradient-${segmentIndex}-${subsegmentIndex}`}
          id={gradientId}
          x1={midpointX}
          y1={midpointY}
          x2={midpointX}
          y2={midpointY - gradientLength}
          gradientUnits="userSpaceOnUse"
        >
          {gradientStopIndexes.map(stopIndex => {
            const stopOffset = stopIndex / CURVE_GRADIENT_STOPS
            const yAtStop = midpointY - stopOffset * gradientLength
            const rawProgress = stripHeight > 0
              ? clamp01((midpointY - yAtStop) / stripHeight)
              : 1
            // Per-subsegment noise wiggles band borders along x.
            const noisyProgress = clamp01(
              rawProgress + sampleBandBoundaryProgressOffset(
                { x: midpointX, y: yAtStop },
                settings
              )
            )
            return (
              <stop
                key={stopIndex}
                offset={`${stopOffset * 100}%`}
                stopColor={grayLevelFill(getGradientGrayAtProgress({
                  progress: noisyProgress,
                  gradientStrength: settings.gradientStrength,
                  gradientCurve: settings.gradientCurve,
                  ditherDensity: settings.ditherDensity,
                  minDitherDensity: settings.minDitherDensity,
                  maxDitherDensity: settings.maxDitherDensity,
                  densityBands: settings.densityBands,
                  bandThicknessMultiplier: settings.bandThicknessMultiplier,
                  bandLowerProgressOverrides: settings.bandLowerProgressOverrides,
                }))}
                stopOpacity={1}
              />
            )
          })}
        </linearGradient>
      )
      bandPaths.push(
        <path
          key={`band-${segmentIndex}-${subsegmentIndex}`}
          d={
            `M ${startX} ${startY} L ${endX} ${endY} ` +
            `L ${endX} ${topY} L ${startX} ${topY} Z`
          }
          fill={`url(#${gradientId})`}
        />
      )
    }
  }

  return (
    <>
      {gradientDefs}
      <g id={surfaceId}>{bandPaths}</g>
    </>
  )
}

const buildNoiseAmplificationMatrix = (settings: BackgroundPatternSettings) => {
  const constantBias = -settings.noiseAmplitude / 2 + settings.noiseOffset
  return [
    `${settings.noiseAmplitude} 0 0 0 ${constantBias}`,
    `${settings.noiseAmplitude} 0 0 0 ${constantBias}`,
    `${settings.noiseAmplitude} 0 0 0 ${constantBias}`,
    `0 0 0 0 1`,
  ].join('  ')
}

const renderContinuousNoisePlusGradientFilter = (
  noiseSource: NoiseSource,
  filterId: string,
  gradientSurfaceId: string,
  bounds: PatternBounds,
  settings: BackgroundPatternSettings
): ReactNode | null => {
  if (noiseSource === 'none') return null
  const turbulenceType =
    noiseSource === 'turbulence' ? 'turbulence' : 'fractalNoise'
  return (
    <filter
      id={filterId}
      x="0%"
      y="0%"
      width="100%"
      height="100%"
      filterUnits="objectBoundingBox"
      primitiveUnits="userSpaceOnUse"
      colorInterpolationFilters="sRGB"
    >
      <feTurbulence
        type={turbulenceType}
        baseFrequency={settings.baseFrequency}
        numOctaves={settings.numOctaves}
        seed={settings.seed}
        stitchTiles="stitch"
        result="rawNoise"
      />
      <feColorMatrix
        in="rawNoise"
        type="matrix"
        values={buildNoiseAmplificationMatrix(settings)}
        result="amplifiedNoise"
      />
      <feImage
        href={`#${gradientSurfaceId}`}
        x={bounds.x}
        y={bounds.y}
        width={bounds.width}
        height={bounds.height}
        preserveAspectRatio="none"
        result="gradientImage"
      />
      <feComposite
        in="amplifiedNoise"
        in2="gradientImage"
        operator="arithmetic"
        k1="0"
        k2="1"
        k3="1"
        k4="0"
      />
    </filter>
  )
}

const smoothstep = (value: number): number =>
  value * value * (3 - 2 * value)

const lerp = (from: number, to: number, amount: number): number =>
  from + (to - from) * amount

const hashNoise = (x: number, y: number, seed: number): number => {
  const raw = Math.sin(x * 127.1 + y * 311.7 + seed * 74.7) * 43758.5453123
  return raw - Math.floor(raw)
}

const valueNoise = (x: number, y: number, seed: number): number => {
  const x0 = Math.floor(x)
  const y0 = Math.floor(y)
  const xFade = smoothstep(x - x0)
  const yFade = smoothstep(y - y0)
  const top = lerp(
    hashNoise(x0, y0, seed),
    hashNoise(x0 + 1, y0, seed),
    xFade
  )
  const bottom = lerp(
    hashNoise(x0, y0 + 1, seed),
    hashNoise(x0 + 1, y0 + 1, seed),
    xFade
  )
  return lerp(top, bottom, yFade)
}

const sampleNoise = (
  x: number,
  y: number,
  settings: BackgroundPatternSettings
): number => {
  let total = 0
  let amplitude = 1
  let amplitudeTotal = 0
  let frequency = settings.baseFrequency
  const octaves = Math.max(1, Math.round(settings.numOctaves))
  for (let octave = 0; octave < octaves; octave += 1) {
    const noise = valueNoise(
      x * frequency,
      y * frequency,
      settings.seed + octave * 101
    )
    const octaveValue = settings.noiseSource === 'turbulence'
      ? Math.abs(noise * 2 - 1)
      : noise
    total += octaveValue * amplitude
    amplitudeTotal += amplitude
    frequency *= 2
    amplitude *= 0.5
  }
  return amplitudeTotal > 0 ? total / amplitudeTotal : 0.5
}

// Band-border noise, independent of noiseSource so borders can carry
// a different texture than the dither. Result in [0, 1].
const sampleBandBoundaryNoiseUnit = (
  point: ScreenPoint,
  settings: BackgroundPatternSettings
): number => {
  const octaves = Math.max(1, Math.round(settings.bandBoundaryNoiseOctaves))
  const seed = settings.seed + 1009
  let total = 0
  let amplitude = 1
  let amplitudeTotal = 0
  let frequencyX = settings.bandBoundaryNoiseFrequencyX
  let frequencyY = settings.bandBoundaryNoiseFrequencyY
  for (let octave = 0; octave < octaves; octave += 1) {
    const raw = valueNoise(
      point.x * frequencyX,
      point.y * frequencyY,
      seed + octave * 101
    )
    const octaveValue = settings.bandBoundaryNoiseType === 'turbulence'
      ? Math.abs(raw * 2 - 1)
      : raw
    total += octaveValue * amplitude
    amplitudeTotal += amplitude
    frequencyX *= 2
    frequencyY *= 2
    amplitude *= 0.5
  }
  const normalized = amplitudeTotal > 0 ? total / amplitudeTotal : 0.5
  const steps = Math.round(settings.bandBoundaryNoiseQuantizeSteps)
  if (steps < 2) return normalized
  // 0.999999 clamp keeps the top bucket from exceeding 1 after /(steps-1).
  return Math.min(
    1,
    Math.floor(Math.min(0.999999, normalized) * steps) / (steps - 1)
  )
}

// Raw-progress perturbation shifting band boundaries; 0 if unbanded.
const sampleBandBoundaryProgressOffset = (
  point: ScreenPoint,
  settings: BackgroundPatternSettings
): number => {
  if (settings.densityBands < 2) return 0
  if (settings.bandBoundaryNoise <= 0) return 0
  const noise = sampleBandBoundaryNoiseUnit(point, settings)
  // bandBoundaryNoise=1 shifts a border by up to ±half a band.
  return ((noise - 0.5) * settings.bandBoundaryNoise) / settings.densityBands
}

const getDensityAtFromLookups = (
  x: number,
  y: number,
  minLineLookup: CurveLookup,
  maxLineLookup: CurveLookup,
  settings: BackgroundPatternSettings,
  anchor: DitherAnchor = 'top'
): number => {
  const gradientDensity = getCurveGradientOpacityAtPointFromLookups(
    { x, y },
    minLineLookup,
    maxLineLookup,
    settings,
    anchor
  )
  if (gradientDensity <= 0) return 0
  const maxDensity = clamp01(settings.maxDitherDensity)
  if (settings.noiseAmplitude === 0 && settings.noiseOffset >= maxDensity) {
    return maxDensity
  }
  if (settings.noiseSource === 'none') return clampDitherDensity(
    gradientDensity,
    settings
  )
  if (settings.noiseAmplitude === 0) {
    return clampDitherDensity(gradientDensity + settings.noiseOffset, settings)
  }
  const noise = sampleNoise(x, y, settings)
  const noisePerturbation =
    noise * settings.noiseAmplitude - settings.noiseAmplitude / 2
      + settings.noiseOffset
  return clampDitherDensity(
    gradientDensity + noisePerturbation,
    settings
  )
}

const getTileDensityFromLookups = (
  x: number,
  y: number,
  tileSize: number,
  minLineLookup: CurveLookup,
  maxLineLookup: CurveLookup,
  settings: BackgroundPatternSettings,
  anchor: DitherAnchor = 'top'
): number => {
  return getDensityAtFromLookups(
    x + tileSize * 0.5,
    y + tileSize * 0.5,
    minLineLookup,
    maxLineLookup,
    settings,
    anchor
  )
}

const forEachTileOrigin = (
  bounds: PatternBounds,
  tileSize: number,
  offsetX: number,
  callback: (x: number, y: number) => void
) => {
  const startX =
    Math.floor((bounds.x - offsetX) / tileSize) * tileSize + offsetX
  const startY = Math.floor(bounds.y / tileSize) * tileSize
  const endX = bounds.x + bounds.width
  const endY = bounds.y + bounds.height
  for (let y = startY; y < endY; y += tileSize) {
    for (let x = startX; x < endX; x += tileSize) {
      callback(x, y)
    }
  }
}

// Horizontal extent of the plot area in graph coordinates. When provided, the
// dot dither lays its columns out as an EXACT FIT across this span: the first
// column's largest dots start flush at `left`, the last column's largest dots
// end flush at `right`, and the column pitch stretches imperceptibly (<1% —
// e.g. a 455px plot with 4px tiles gets a ~3.996px pitch) so no dot is ever
// clipped at either plot edge. Omitted → legacy fixed-pitch offsetX grid.
export type PlotSpanX = { left: number; right: number }

type EdgeFlushColumnLayout = {
  firstCenterX: number
  pitch: number
  count: number
}

// Antialiasing feather half-width, in css px: the GL shader renders partial
// coverage out to half an AA band PAST a dot's geometric radius, so an
// edge-flush dot whose geometric edge sits exactly ON the plot edge gets
// that outer feather clipped by the canvas — visible as a subtly flattened
// side on the first/last columns (the HUD graph has plot left padding 0).
// The layout insets dot centers by this margin so the feather always fits.
// Worst case is dpr 1, where the GL feather band is 1 css px wide.
const DOT_EDGE_AA_INSET_PX = 0.5

// Exact-fit column layout for `PlotSpanX`: dot centers run from
// `left + maxDotRadius` to `right - maxDotRadius` (plus the AA inset above),
// so a full-size dot in the first/last column meets the plot edge exactly
// without being cut off — antialiasing feather included.
const createEdgeFlushColumnLayout = (
  plotSpanX: PlotSpanX,
  tileSize: number,
  maxDotRadius: number,
): EdgeFlushColumnLayout => {
  const firstCenterX = plotSpanX.left + maxDotRadius + DOT_EDGE_AA_INSET_PX
  const lastCenterX = Math.max(
    firstCenterX,
    plotSpanX.right - maxDotRadius - DOT_EDGE_AA_INSET_PX,
  )
  const innerSpan = lastCenterX - firstCenterX
  const gapCount = Math.max(1, Math.round(innerSpan / tileSize))
  return {
    firstCenterX,
    pitch: innerSpan / gapCount,
    count: gapCount + 1,
  }
}

// Iterates the dot-dither grid: rows at the legacy tileSize stepping, columns
// either at the legacy fixed pitch (ditherPatternOffsetX phase) or, when an
// edge-flush layout is given, at the exact-fit positions clamped to the plot
// span. The callback receives the tile ORIGIN (center - tileSize/2, the legacy
// convention) plus the column index driving the 2x2 parity path split. Both
// the static dither and the hovered-band highlight iterate through here, so
// their tiles stay perfectly aligned.
const forEachDotTileOrigin = (
  bounds: PatternBounds,
  tileSize: number,
  offsetX: number,
  columnLayout: EdgeFlushColumnLayout | null,
  callback: (x: number, y: number, columnIndex: number) => void
) => {
  if (!columnLayout) {
    forEachTileOrigin(bounds, tileSize, offsetX, (x, y) => {
      callback(x, y, Math.round((x - offsetX) / tileSize))
    })
    return
  }
  const { firstCenterX, pitch, count } = columnLayout
  const startY = Math.floor(bounds.y / tileSize) * tileSize
  const endY = bounds.y + bounds.height
  // Only visit the columns that overlap the raster bounds horizontally.
  const firstColumnIndex = Math.max(
    0,
    Math.floor((bounds.x - firstCenterX) / pitch),
  )
  const lastColumnIndex = Math.min(
    count - 1,
    Math.ceil((bounds.x + bounds.width - firstCenterX) / pitch),
  )
  for (let y = startY; y < endY; y += tileSize) {
    for (
      let columnIndex = firstColumnIndex;
      columnIndex <= lastColumnIndex;
      columnIndex += 1
    ) {
      const x = firstCenterX + columnIndex * pitch - tileSize / 2
      callback(x, y, columnIndex)
    }
  }
}

const getRasterBounds = (bounds: PatternBounds): PatternBounds => {
  const x = Math.floor(bounds.x)
  const y = Math.floor(bounds.y)
  const right = Math.ceil(bounds.x + bounds.width)
  const bottom = Math.ceil(bounds.y + bounds.height)
  return {
    x,
    y,
    width: Math.max(1, right - x),
    height: Math.max(1, bottom - y),
  }
}

// Intersects pattern bounds with the visible [0,width]×[0,height] rect, with
// one tile of margin so edge tiles still paint their overhang. Tiles are laid
// on an absolute grid (multiples of tileSize — see forEachDotTileOrigin), so
// shrinking the bounds only drops tiles that could never appear on canvas.
const intersectBoundsWithViewport = (
  bounds: PatternBounds,
  viewportWidth: number,
  viewportHeight: number,
  tileMargin: number,
): PatternBounds => {
  const left = Math.max(bounds.x, -tileMargin)
  const top = Math.max(bounds.y, -tileMargin)
  const right = Math.min(bounds.x + bounds.width, viewportWidth + tileMargin)
  const bottom = Math.min(bounds.y + bounds.height, viewportHeight + tileMargin)
  return {
    x: left,
    y: top,
    width: Math.max(1, right - left),
    height: Math.max(1, bottom - top),
  }
}

const doRectsOverlap = (first: DOMRect, second: DOMRect): boolean => {
  return first.left < second.right
    && first.right > second.left
    && first.top < second.bottom
    && first.bottom > second.top
}

const isElementAndAncestorsVisible = (element: HTMLElement): boolean => {
  let current: HTMLElement | null = element
  while (current) {
    const style = window.getComputedStyle(current)
    if (style.display === 'none') return false
    if (style.visibility === 'hidden') return false
    const opacity = Number.parseFloat(style.opacity)
    if (Number.isFinite(opacity) && opacity <= 0.001) return false
    current = current.parentElement
  }
  return true
}

export const getVisibleGlobeGeometryCanvases = (): HTMLCanvasElement[] => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return []
  }
  return Array.from(
    document.querySelectorAll<HTMLCanvasElement>(
      `${GLOBE_GEOMETRY_SOURCE_SELECTOR} canvas`
    )
  ).filter(element => {
    const rect = element.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return false
    if (element.width <= 0 || element.height <= 0) return false
    return isElementAndAncestorsVisible(element)
  })
}

const getAlphaMaskSignature = (
  data: Uint8ClampedArray,
  width: number,
  height: number,
  padding: number
): { alphaPixelCount: number; signature: string } => {
  let hash = 2166136261
  let alphaPixelCount = 0
  for (let index = 3; index < data.length; index += 4) {
    const alpha = data[index]
    if (alpha > 8) alphaPixelCount += 1
    hash = Math.imul(hash ^ alpha, 16777619)
  }
  return {
    alphaPixelCount,
    signature: `${width}x${height}:${padding}:${hash >>> 0}`,
  }
}

const getGlobeDotExclusionMaskSnapshot = (
  sourceElement: Element,
  rasterBounds: PatternBounds,
  paddingPx: number
): GlobeDotExclusionMaskSnapshot => {
  const sourceRect = sourceElement.getBoundingClientRect()
  if (sourceRect.width <= 0 || sourceRect.height <= 0) {
    return { mask: null, signature: 'empty-canvas' }
  }
  const maskWidth = Math.max(1, Math.ceil(rasterBounds.width))
  const maskHeight = Math.max(1, Math.ceil(rasterBounds.height))
  const scaleX = maskWidth / sourceRect.width
  const scaleY = maskHeight / sourceRect.height
  const padding = paddingPx * ((scaleX + scaleY) / 2)
  const maskCanvas = document.createElement('canvas')
  maskCanvas.width = maskWidth
  maskCanvas.height = maskHeight
  const maskContext = maskCanvas.getContext('2d', { willReadFrequently: true })
  if (!maskContext) return { mask: null, signature: 'no-mask-context' }

  const rectSignatureParts: string[] = []
  for (const globeCanvas of getVisibleGlobeGeometryCanvases()) {
    const globeRect = globeCanvas.getBoundingClientRect()
    if (!doRectsOverlap(sourceRect, globeRect)) continue
    const drawX = (globeRect.left - sourceRect.left) * scaleX
    const drawY = (globeRect.top - sourceRect.top) * scaleY
    const drawWidth = globeRect.width * scaleX
    const drawHeight = globeRect.height * scaleY
    rectSignatureParts.push([
      drawX,
      drawY,
      drawWidth,
      drawHeight,
    ].map(value => (
      Math.round(value * GLOBE_EXCLUSION_SIGNATURE_PRECISION)
        / GLOBE_EXCLUSION_SIGNATURE_PRECISION
    )).join(','))
    try {
      maskContext.drawImage(
        globeCanvas,
        drawX,
        drawY,
        drawWidth,
        drawHeight
      )
    } catch {
      return { mask: null, signature: 'globe-canvas-unreadable' }
    }
  }

  let imageData: ImageData
  try {
    imageData = maskContext.getImageData(0, 0, maskWidth, maskHeight)
  } catch {
    return { mask: null, signature: 'globe-mask-tainted' }
  }
  const { alphaPixelCount, signature } = getAlphaMaskSignature(
    imageData.data,
    maskWidth,
    maskHeight,
    padding
  )
  if (alphaPixelCount === 0) {
    return { mask: null, signature: `empty:${rectSignatureParts.join('|')}` }
  }
  return {
    mask: {
      width: maskWidth,
      height: maskHeight,
      padding,
      data: imageData.data,
    },
    signature: `${signature}:${rectSignatureParts.join('|')}`,
  }
}

const doesDotOverlapGlobeGeometry = (
  dotCenterX: number,
  dotCenterY: number,
  dotRadius: number,
  globeDotExclusionMask: GlobeDotExclusionMask | null
): boolean => {
  if (!globeDotExclusionMask) return false
  const sampleRadius = Math.max(0, dotRadius + globeDotExclusionMask.padding)
  const sampleRadiusSquared = sampleRadius * sampleRadius
  const minX = Math.max(0, Math.floor(dotCenterX - sampleRadius))
  const maxX = Math.min(
    globeDotExclusionMask.width - 1,
    Math.ceil(dotCenterX + sampleRadius)
  )
  const minY = Math.max(0, Math.floor(dotCenterY - sampleRadius))
  const maxY = Math.min(
    globeDotExclusionMask.height - 1,
    Math.ceil(dotCenterY + sampleRadius)
  )
  for (let sampleY = minY; sampleY <= maxY; sampleY += 1) {
    for (let sampleX = minX; sampleX <= maxX; sampleX += 1) {
      const dx = sampleX + 0.5 - dotCenterX
      const dy = sampleY + 0.5 - dotCenterY
      if (dx * dx + dy * dy > sampleRadiusSquared) continue
      const alphaIndex = (sampleY * globeDotExclusionMask.width + sampleX) * 4 + 3
      if (globeDotExclusionMask.data[alphaIndex] > 8) return true
    }
  }
  return false
}

const getDistanceSquaredToSegment = (
  point: ScreenPoint,
  segmentStart: ScreenPoint,
  segmentEnd: ScreenPoint
): number => {
  const segmentX = segmentEnd.x - segmentStart.x
  const segmentY = segmentEnd.y - segmentStart.y
  const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY
  if (segmentLengthSquared === 0) {
    const dx = point.x - segmentStart.x
    const dy = point.y - segmentStart.y
    return dx * dx + dy * dy
  }
  const projection = (
    (point.x - segmentStart.x) * segmentX
    + (point.y - segmentStart.y) * segmentY
  ) / segmentLengthSquared
  const clampedProjection = Math.max(0, Math.min(1, projection))
  const closestX = segmentStart.x + segmentX * clampedProjection
  const closestY = segmentStart.y + segmentY * clampedProjection
  const dx = point.x - closestX
  const dy = point.y - closestY
  return dx * dx + dy * dy
}

const createPolylineSegments = (polylines: ScreenPoint[][]): PolylineSegment[] => {
  const segments: PolylineSegment[] = []
  for (const points of polylines) {
    for (let pointIndex = 0; pointIndex < points.length - 1; pointIndex += 1) {
      const start = points[pointIndex]
      const end = points[pointIndex + 1]
      segments.push({
        start,
        end,
        minX: Math.min(start.x, end.x),
        maxX: Math.max(start.x, end.x),
        minY: Math.min(start.y, end.y),
        maxY: Math.max(start.y, end.y),
      })
    }
  }
  return segments.sort((first, second) => first.minX - second.minX)
}

// Binary search (lower_bound) for the first segment with minX >= targetX, over
// segments sorted ascending by minX.
const firstSegmentIndexAtOrAfterX = (
  segments: PolylineSegment[],
  targetX: number
): number => {
  let lo = 0
  let hi = segments.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (segments[mid].minX < targetX) lo = mid + 1
    else hi = mid
  }
  return lo
}

const getClosestDistanceSquaredToPolylineSegments = (
  dotCenter: ScreenPoint,
  segments: PolylineSegment[],
  maxSegmentWidthX: number,
  influenceRadius: number
): number | null => {
  if (segments.length === 0) return null
  let closestDistanceSquared = Number.POSITIVE_INFINITY
  const minX = dotCenter.x - influenceRadius
  const maxX = dotCenter.x + influenceRadius
  const minY = dotCenter.y - influenceRadius
  const maxY = dotCenter.y + influenceRadius
  // Any segment with minX < minX - maxSegmentWidthX has maxX < minX, so it
  // can't overlap the tile's influence box — skip straight past them.
  const startIndex = firstSegmentIndexAtOrAfterX(segments, minX - maxSegmentWidthX)
  for (let segmentIndex = startIndex; segmentIndex < segments.length; segmentIndex += 1) {
    const segment = segments[segmentIndex]
    if (segment.minX > maxX) break
    if (segment.maxX < minX) continue
    if (segment.minY > maxY) continue
    if (segment.maxY < minY) continue
    const distanceSquared = getDistanceSquaredToSegment(
      dotCenter,
      segment.start,
      segment.end
    )
    if (distanceSquared < closestDistanceSquared) {
      closestDistanceSquared = distanceSquared
    }
  }
  if (!Number.isFinite(closestDistanceSquared)) return null
  return closestDistanceSquared
}

const getPolylineDotRadiusMultiplier = (
  dotCenter: ScreenPoint,
  dotRadius: number,
  exclusion: PolylineDotExclusion | null
): number => {
  if (!exclusion) return 1
  let multiplier = 1
  for (const group of exclusion.groups) {
    const influenceRadius = Math.max(0, dotRadius + group.padding)
    if (influenceRadius <= 0) continue
    const distanceSquared = getClosestDistanceSquaredToPolylineSegments(
      dotCenter,
      group.segments,
      group.maxSegmentWidthX,
      influenceRadius
    )
    if (distanceSquared === null) continue
    const influenceRadiusSquared = influenceRadius * influenceRadius
    if (distanceSquared >= influenceRadiusSquared) continue
    const distance = Math.sqrt(distanceSquared)
    const overlapProgress = clamp01(1 - distance / influenceRadius)
    const groupMultiplier = 1 - overlapProgress * (1 - group.radiusMultiplier)
    if (groupMultiplier < multiplier) multiplier = groupMultiplier
  }
  return multiplier
}

// radius = min(cap, max(base*multiplier, base+additivePx)); additive
// floor guarantees a visible bump for tiny low-density dots.
type DotThicknessAdjustment = {
  dotRadiusMultiplier: number
  dotRadiusAdditivePx: number
  // Post-floor/cap multiplier — the only way to SHRINK dots (the pair above
  // is floored at the base radius). The hover dot-shrink dim drives it; see
  // applyDotThicknessAdjustment.
  dotRadiusScale?: number
}

const NO_DOT_THICKNESS_ADJUSTMENT: DotThicknessAdjustment = {
  dotRadiusMultiplier: 1,
  dotRadiusAdditivePx: 0,
}

// Declarative form of a layer's confidence→density field, for the GPU bake
// (computeDitherFieldGLLayer). Unlike the opaque per-tile TileShadeField
// closure, this exposes the field's separable structure — per-column /
// per-row parameters combined by a few flops per tile — which makes the bake
// ~an order of magnitude cheaper than calling a closure per tile.
export type DitherShadeModel =
  | {
      // Density from the band progress (0 at the min line, 1 at the max/top
      // line) mapped through confidence→density stops. The red field.
      kind: 'progressStops'
      stops: ConfidenceDensityStop[]
    }
  | {
      // Density from the trailing-actor CDF: confidence at (year(x), cap(y))
      // mapped through the stops. The black field.
      kind: 'trailingCdf'
      survivalYears: number
      stops: ConfidenceDensityStop[]
      yearForScreenX: (screenX: number) => number
      valueForY: (screenY: number) => number
    }

export type RedAboveLossOfControlCanvasLayer = {
  pathRender: RedAboveLossOfControlPathRender
  settings: BackgroundPatternSettings
  fillColor: string
  opacity: number
  capabilityLineScreenPoints?: ScreenPoint[]
  additionalCapabilityLineScreenPointsList?: ScreenPoint[][]
  // Mid-fade carve polylines (the plan-preview fan); see the type's comment.
  fadingCapabilityLinePolylines?: FadingCapabilityLinePolylines | null
  thicknessAdjustment?: DotThicknessAdjustment
  restrictToBandIndex?: number | null
  clipPathData?: string
  // Omitted/'top' keeps red-above byte-for-byte; 'bottom' = floor field.
  anchor?: DitherAnchor
  // Plot-area horizontal extent for the edge-flush exact-fit dot columns.
  // See PlotSpanX. Omitted → legacy fixed-pitch grid.
  plotSpanX?: PlotSpanX
  // When set, each dot's density comes from this field instead of the min/max
  // gradient (the trailing-actor CDF: shade = confidence). See TileShadeField.
  tileShadeField?: TileShadeField | null
  // Structured description of tileShadeField for the GPU bake's fast path;
  // keep the two in sync (tileShadeField remains the source of truth for the
  // canvas-2D fallback and the hover inspector).
  shadeModel?: DitherShadeModel | null
}

const applyDotThicknessAdjustment = (
  baseRadius: number,
  radiusCap: number,
  adjustment: DotThicknessAdjustment
): number => {
  const scaled = baseRadius * adjustment.dotRadiusMultiplier
  const additive = baseRadius + adjustment.dotRadiusAdditivePx
  // dotRadiusScale applies AFTER the floor/cap: the multiplier/additive pair
  // can only thicken (the base radius floors it), so shrinking — the hover
  // dot-shrink dim (riskZoneDimDotShrink) — needs this uncapped scale. Both
  // dither paths (canvas-2D drawDotDither and the GL bake's densityToRadius)
  // come through here, so the scale reaches every dot.
  return (
    Math.min(radiusCap, Math.max(scaled, additive))
    * (adjustment.dotRadiusScale ?? 1)
  )
}

const getMaxDotRadiusForSettings = (
  settings: BackgroundPatternSettings,
  tileSize: number
): number => {
  const densityCap = clamp01(settings.maxDitherDensity)
  return tileSize * Math.sqrt(densityCap / Math.PI)
}

// Per-band dot radii, each >= prev + minDotRadiusStepPx, capped at
// the maxDitherDensity radius. Null when unbanded or step is 0.
const computeBandDotRadii = (
  settings: BackgroundPatternSettings,
  tileSize: number,
  thicknessAdjustment: DotThicknessAdjustment
): number[] | null => {
  if (settings.densityBands < 2) return null
  if (settings.minDotRadiusStepPx <= 0) return null
  const radiusCap = getMaxDotRadiusForSettings(settings, tileSize)
  const radii: number[] = []
  let prevRadius = 0
  for (
    let bandIndex = 0;
    bandIndex < settings.densityBands;
    bandIndex += 1
  ) {
    const bandedProgress = bandIndex / (settings.densityBands - 1)
    const bandDensity = getGradientGrayAtProgress({
      progress: bandedProgress,
      gradientStrength: settings.gradientStrength,
      gradientCurve: settings.gradientCurve,
      ditherDensity: settings.ditherDensity,
      minDitherDensity: settings.minDitherDensity,
      maxDitherDensity: settings.maxDitherDensity,
      densityBands: 0,
      bandThicknessMultiplier: settings.bandThicknessMultiplier,
    })
    const naturalRadius = tileSize * Math.sqrt(bandDensity / Math.PI)
    const adjustedRadius = applyDotThicknessAdjustment(
      naturalRadius,
      radiusCap,
      thicknessAdjustment
    )
    const steppedRadius = Math.max(
      adjustedRadius,
      prevRadius + settings.minDotRadiusStepPx
    )
    const radius = Math.min(radiusCap, steppedRadius)
    radii.push(radius)
    prevRadius = radius
  }
  return radii
}

const getBandIndexAtTileCenterFromLookups = (
  tileCenter: ScreenPoint,
  minLineLookup: CurveLookup,
  maxLineLookup: CurveLookup,
  settings: BackgroundPatternSettings,
  anchor: DitherAnchor = 'top'
): number | null => {
  if (settings.densityBands < 2) return null
  const progress = getRawProgressAtPointFromLookups(
    tileCenter, minLineLookup, maxLineLookup, anchor
  )
  if (progress === null) return null
  const noisyProgress = clamp01(
    progress + sampleBandBoundaryProgressOffset(tileCenter, settings)
  )
  return getBandIndexFromProgress(
    noisyProgress,
    settings.densityBands,
    settings.bandThicknessMultiplier,
    settings.bandLowerProgressOverrides
  )
}

const getPositiveRemainder = (value: number, divisor: number): number =>
  ((value % divisor) + divisor) % divisor


// A per-tile density source in [0,1], keyed on graph-space screen coords. When
// supplied it replaces the min/max-band gradient entirely — the caller drives
// each dot's darkness from an external field (the trailing-actor CDF: shade =
// P(reached ≥ this tile's capability), see TileShadeField usage below).
export type TileShadeField = (screenX: number, screenY: number) => number

const drawDotDither = (
  context: CanvasRenderingContext2D,
  rasterBounds: PatternBounds,
  minLineLookup: CurveLookup,
  maxLineLookup: CurveLookup,
  settings: BackgroundPatternSettings,
  cellSize: number,
  thicknessAdjustment: DotThicknessAdjustment,
  restrictToBandIndex: number | null,
  globeDotExclusionMask: GlobeDotExclusionMask | null,
  capabilityLineDotExclusion: PolylineDotExclusion | null,
  anchor: DitherAnchor = 'top',
  plotSpanX: PlotSpanX | null = null,
  tileShadeField: TileShadeField | null = null,
  // Unclamped horizontal extent for the flush-edge alignment lerp when
  // rasterBounds was viewport-clamped (see drawFinalDitherToContext).
  alignSpan: { x: number; width: number } | null = null
) => {
  const tileSize = cellSize * 2
  const radiusCap = getMaxDotRadiusForSettings(settings, tileSize)
  const columnLayout = plotSpanX
    ? createEdgeFlushColumnLayout(plotSpanX, tileSize, radiusCap)
    : null
  const bandDotRadii = computeBandDotRadii(
    settings, tileSize, thicknessAdjustment
  )
  const needTileBandIndex = bandDotRadii !== null || restrictToBandIndex !== null

  // Band centers (raw-progress) for the continuous radius blend; only
  // used when bandDotRadiusBlendAmount > 0, else discrete per-band.
  const bandBlendAmount = settings.bandDotRadiusBlendAmount ?? 0
  const bandCenters: number[] | null =
    bandDotRadii && bandBlendAmount > 0
      ? Array.from({ length: settings.densityBands }, (_, i) => {
          const lo = getBandLowerProgress(
            i,
            settings.densityBands,
            settings.bandThicknessMultiplier,
            settings.bandLowerProgressOverrides,
          )
          const up = getBandLowerProgress(
            i + 1,
            settings.densityBands,
            settings.bandThicknessMultiplier,
            settings.bandLowerProgressOverrides,
          )
          return (lo + up) / 2
        })
      : null

  // Linear interp between band-center radii bracketing progress.
  const continuousBandRadius = (progress: number): number => {
    if (!bandDotRadii || !bandCenters) return 0
    if (bandCenters.length === 1) return bandDotRadii[0]
    if (progress <= bandCenters[0]) return bandDotRadii[0]
    if (progress >= bandCenters[bandCenters.length - 1]) {
      return bandDotRadii[bandDotRadii.length - 1]
    }
    for (let i = 0; i < bandCenters.length - 1; i++) {
      if (progress >= bandCenters[i] && progress <= bandCenters[i + 1]) {
        const span = bandCenters[i + 1] - bandCenters[i]
        const t = span > 0 ? (progress - bandCenters[i]) / span : 0
        return bandDotRadii[i] + t * (bandDotRadii[i + 1] - bandDotRadii[i])
      }
    }
    return bandDotRadii[0]
  }
  const dotPaths = Array.from({ length: 4 }, () => new Path2D())
  const dotPathHasContent = Array.from({ length: 4 }, () => false)
  forEachDotTileOrigin(
    rasterBounds,
    tileSize,
    settings.ditherPatternOffsetX,
    columnLayout,
    (x, y, columnIndex) => {
      let radius: number
      if (tileShadeField) {
        // Density comes straight from the external shade field (the trailing-
        // actor CDF), not the min/max-band gradient: shade = confidence at this
        // tile's capability/year.
        const shade = tileShadeField(x + tileSize / 2, y + tileSize / 2)
        if (shade <= 0) return
        const naturalRadius = tileSize * Math.sqrt(clamp01(shade) / Math.PI)
        radius = applyDotThicknessAdjustment(naturalRadius, radiusCap, thicknessAdjustment)
      } else {
      const tileBandIndex = needTileBandIndex
        ? getBandIndexAtTileCenterFromLookups(
            { x: x + tileSize / 2, y: y + tileSize / 2 },
            minLineLookup,
            maxLineLookup,
            settings,
            anchor
          )
        : null
      // Tile-perfect band filter: exact subset of the main dither's tiles.
      if (restrictToBandIndex !== null && tileBandIndex !== restrictToBandIndex) {
        return
      }
      if (bandDotRadii) {
        if (tileBandIndex === null) return
        const discreteRadius = bandDotRadii[tileBandIndex]
        if (bandCenters && bandBlendAmount > 0) {
          // Blend discrete per-band radius toward the continuous interp.
          const tileCenterPoint = {
            x: x + tileSize / 2,
            y: y + tileSize / 2,
          }
          const rawProgress = getRawProgressAtPointFromLookups(
            tileCenterPoint,
            minLineLookup,
            maxLineLookup,
            anchor,
          )
          if (rawProgress !== null) {
            const noisyProgress = clamp01(
              rawProgress
                + sampleBandBoundaryProgressOffset(tileCenterPoint, settings),
            )
            const continuous = continuousBandRadius(noisyProgress)
            radius =
              discreteRadius
              + bandBlendAmount * (continuous - discreteRadius)
          } else {
            radius = discreteRadius
          }
        } else {
          radius = discreteRadius
        }
      } else {
        const baseDensity = getTileDensityFromLookups(
          x,
          y,
          tileSize,
          minLineLookup,
          maxLineLookup,
          settings,
          anchor
        )
        if (baseDensity <= 0) return
        const naturalRadius = tileSize * Math.sqrt(baseDensity / Math.PI)
        radius = applyDotThicknessAdjustment(
          naturalRadius, radiusCap, thicknessAdjustment
        )
      }
      }
      if (radius <= 0) return
      // Lerp dot alignment flush-left→flush-right so both side edges
      // stay flush as dots shrink. alignT: 0 at span left, 1 at right.
      // Spans the UNCLAMPED field when the bounds were viewport-clamped,
      // so clamping never moves a dot (matches the GL bake's uFieldWidth).
      // With the edge-flush column layout the span is the plot span itself,
      // so flush is exact at the plot edges.
      const alignOriginX = plotSpanX
        ? plotSpanX.left
        : alignSpan ? alignSpan.x : rasterBounds.x
      const alignWidth = plotSpanX
        ? plotSpanX.right - plotSpanX.left
        : alignSpan ? alignSpan.width : rasterBounds.width
      const tileLeftX = x - rasterBounds.x
      const alignT = alignWidth > 0
        ? clamp01((x - alignOriginX) / alignWidth)
        : 0
      // Edge-flush columns: slide within the column's max-dot extent
      // (center ± (radiusCap - radius)), NOT within the tile — the first/last
      // column centers sit radiusCap in from the plot edges, so this keeps
      // shrinking dots flush at the edges without ever crossing them (a
      // tile-relative slide pushes dots past the edge whenever
      // radiusCap < tileSize/2, cutting them off at padding-0 plots).
      // Legacy grid: slide within the tile, as before.
      const dotCenterX = columnLayout
        ? tileLeftX + tileSize / 2
          + (2 * alignT - 1) * Math.max(0, radiusCap - radius)
        : tileLeftX + lerp(radius, tileSize - radius, alignT)
      const dotCenterY = y - rasterBounds.y + tileSize / 2
      const dotCenterScreenPoint = {
        x: rasterBounds.x + dotCenterX,
        y: rasterBounds.y + dotCenterY,
      }
      if (
        doesDotOverlapGlobeGeometry(
          dotCenterX,
          dotCenterY,
          radius,
          globeDotExclusionMask
        )
      ) {
        return
      }
      const lineRadiusMultiplier = getPolylineDotRadiusMultiplier(
        dotCenterScreenPoint,
        radius,
        capabilityLineDotExclusion
      )
      if (lineRadiusMultiplier <= 0) return
      if (lineRadiusMultiplier < 1) {
        radius *= lineRadiusMultiplier
      }
      if (radius <= 0) return
      const rowIndex = Math.round(y / tileSize)
      const dotPathIndex =
        getPositiveRemainder(rowIndex, 2) * 2
        + getPositiveRemainder(columnIndex, 2)
      const dotPath = dotPaths[dotPathIndex]
      if (settings.dotShape === 'square') {
        // Square halftone cell centered on the same point, with half-side
        // equal to the dot radius so it tracks the same size controls.
        const side = radius * 2
        dotPath.rect(dotCenterX - radius, dotCenterY - radius, side, side)
      } else {
        dotPath.moveTo(dotCenterX + radius, dotCenterY)
        dotPath.arc(
          dotCenterX,
          dotCenterY,
          radius,
          0,
          Math.PI * 2
        )
      }
      dotPathHasContent[dotPathIndex] = true
    }
  )
  dotPaths.forEach((dotPath, dotPathIndex) => {
    if (dotPathHasContent[dotPathIndex]) {
      context.fill(dotPath)
    }
  })
}

const drawMatrixDither = (
  context: CanvasRenderingContext2D,
  rasterBounds: PatternBounds,
  minLineLookup: CurveLookup,
  maxLineLookup: CurveLookup,
  settings: BackgroundPatternSettings,
  matrix: ThresholdOrderMatrix,
  cellSize: number,
  thicknessAdjustment: DotThicknessAdjustment,
  restrictToBandIndex: number | null,
  anchor: DitherAnchor = 'top'
): void => {
  const dimension = matrix.length
  const totalCells = dimension * dimension
  const tileSize = dimension * cellSize
  // radius ∝ sqrt(density), so a radius multiplier ≡ density boost = m²−1.
  const densityBoost = Math.max(
    0,
    thicknessAdjustment.dotRadiusMultiplier
      * thicknessAdjustment.dotRadiusMultiplier
      - 1
  )
  const maxDensity = clamp01(settings.maxDitherDensity)
  forEachTileOrigin(
    rasterBounds,
    tileSize,
    settings.ditherPatternOffsetX,
    (x, y) => {
      // Tile-perfect band filter, as in drawDotDither.
      if (restrictToBandIndex !== null) {
        const tileBandIndex = getBandIndexAtTileCenterFromLookups(
          { x: x + tileSize / 2, y: y + tileSize / 2 },
          minLineLookup,
          maxLineLookup,
          settings,
          anchor
        )
        if (tileBandIndex !== restrictToBandIndex) return
      }
      const baseDensity = getTileDensityFromLookups(
        x,
        y,
        tileSize,
        minLineLookup,
        maxLineLookup,
        settings,
        anchor
      )
      const boostedDensity = clamp01(baseDensity * (1 + densityBoost))
      const density = Math.min(maxDensity, boostedDensity)
      const level = Math.round(density * totalCells)
      if (level <= 0) return
      for (let rowIndex = 0; rowIndex < dimension; rowIndex += 1) {
        for (let columnIndex = 0; columnIndex < dimension; columnIndex += 1) {
          if (matrix[rowIndex][columnIndex] >= level) continue
          context.fillRect(
            x - rasterBounds.x + columnIndex * cellSize,
            y - rasterBounds.y + rowIndex * cellSize,
            cellSize,
            cellSize
          )
        }
      }
    }
  )
}

// Builds two complementary clip regions (in raster-local coords, the space the
// dither dots are drawn in) split by the capability polyline: `belowClip`
// covers everything under-or-right of the line down to the bottom edge, and
// `aboveClip` covers everything over-or-left up to the top edge. Because the
// capability line hugs the bottom then rises steeply, "below the line" works
// out to the full-height strip past the rise — exactly the "at risk" region.
const buildCapabilitySplitClipPaths = (
  capabilityLineScreenPoints: ScreenPoint[],
  rasterBounds: PatternBounds,
): { aboveClip: Path2D; belowClip: Path2D } => {
  const { width, height } = rasterBounds
  const localPoints = capabilityLineScreenPoints.map((point) => ({
    x: point.x - rasterBounds.x,
    y: point.y - rasterBounds.y,
  }))
  const first = localPoints[0]
  const last = localPoints[localPoints.length - 1]
  // Trace the polyline, extended flat to both side edges of the raster.
  const traceLine = (path: Path2D) => {
    path.moveTo(0, first.y)
    for (const point of localPoints) path.lineTo(point.x, point.y)
    path.lineTo(width, last.y)
  }
  const belowClip = new Path2D()
  traceLine(belowClip)
  belowClip.lineTo(width, height)
  belowClip.lineTo(0, height)
  belowClip.closePath()
  const aboveClip = new Path2D()
  traceLine(aboveClip)
  aboveClip.lineTo(width, 0)
  aboveClip.lineTo(0, 0)
  aboveClip.closePath()
  return { aboveClip, belowClip }
}

const drawFinalDitherToContext = (
  context: CanvasRenderingContext2D,
  rasterBounds: PatternBounds,
  minLineScreenPoints: ScreenPoint[],
  maxLineScreenPoints: ScreenPoint[],
  capabilityLineScreenPoints: ScreenPoint[],
  settings: BackgroundPatternSettings,
  fillColor: string,
  translateToRasterOrigin: boolean,
  thicknessAdjustment: DotThicknessAdjustment,
  restrictToBandIndex: number | null,
  globeDotExclusionMask: GlobeDotExclusionMask | null,
  capabilityLineDotExclusion: PolylineDotExclusion | null,
  clipPathData?: string,
  anchor: DitherAnchor = 'top',
  plotSpanX: PlotSpanX | null = null,
  tileShadeField: TileShadeField | null = null,
  // When rasterBounds was viewport-clamped for tile enumeration, the ORIGINAL
  // (unclamped) horizontal extent — the dot alignment lerp must span the full
  // field or clamping would shift every dot's sub-tile x position (the GL
  // bake keeps uRasterOriginX/uFieldWidth unclamped the same way).
  alignSpan: { x: number; width: number } | null = null
) => {
  const setupStart = performance.now()
  context.save()
  if (translateToRasterOrigin) {
    context.translate(rasterBounds.x, rasterBounds.y)
  }
  context.fillStyle = fillColor
  const cellSize = Math.max(1, Math.round(settings.patternScale))
  const minLineLookup = createCurveLookup(minLineScreenPoints)
  // When `ditherFillToTop` is on for the top-anchored field, anchor the
  // gradient's max-density edge at the chart top (y=0) instead of the upper
  // loss-of-control line, so the dots taper continuously from the very top down
  // to the curve with no solid plateau above the upper risk line.
  const gradientMaxLineScreenPoints =
    settings.ditherFillToTop && anchor === 'top'
      ? maxLineScreenPoints.map((point) => ({ x: point.x, y: 0 }))
      : maxLineScreenPoints
  const maxLineLookup = createCurveLookup(gradientMaxLineScreenPoints)

  if (clipPathData) {
    try {
      const clipPath = new Path2D(clipPathData)
      context.translate(-rasterBounds.x, -rasterBounds.y)
      try {
        context.clip(clipPath)
      } finally {
        context.translate(rasterBounds.x, rasterBounds.y)
      }
    } catch {
      // Safari Path2D-from-SVG can throw on a malformed path; leave dither unclipped.
    }
  }

  // Draws the pattern with a (possibly side-specific) settings object. cellSize
  // and the min/max line lookups are shared across both sides; only the density
  // fields differ for the safe side.
  const drawPattern = (patternSettings: BackgroundPatternSettings) => {
    if (patternSettings.ditherPattern === 'dots') {
      drawDotDither(
        context,
        rasterBounds,
        minLineLookup,
        maxLineLookup,
        patternSettings,
        cellSize,
        thicknessAdjustment,
        restrictToBandIndex,
        globeDotExclusionMask,
        capabilityLineDotExclusion,
        anchor,
        plotSpanX,
        tileShadeField,
        alignSpan
      )
      return
    }
    const matrix = getOrderMatrixForDitherPattern(patternSettings.ditherPattern)
    if (matrix) {
      drawMatrixDither(
        context,
        rasterBounds,
        minLineLookup,
        maxLineLookup,
        patternSettings,
        matrix,
        cellSize,
        thicknessAdjustment,
        restrictToBandIndex,
        anchor
      )
    }
  }

  // When the capability risk split is on (and this is the full pattern, not a
  // band-highlight subset), paint the dither twice: the safe side clipped above
  // the capability line in `capabilitySafeSideColor` (with its own density
  // overrides), the risk side clipped below/right in the overlay `fillColor`.
  // Default-off → single pass, unchanged.
  const splitClips =
    settings.capabilityRiskSplitEnabled
    && restrictToBandIndex === null
    && capabilityLineScreenPoints.length >= 2
      ? buildCapabilitySplitClipPaths(capabilityLineScreenPoints, rasterBounds)
      : null

  const patternStart = performance.now()
  devPerfMeasure(`dither:${anchor}:setup`, { start: setupStart, end: patternStart })
  if (splitClips) {
    // Safe (upper-left) side uses its own density so it can read sparser/denser
    // than the risk side; all other settings (cell size, banding, …) are shared.
    const safeSideSettings: BackgroundPatternSettings = {
      ...settings,
      ditherDensity: settings.safeSideDitherDensity,
      minDitherDensity: settings.safeSideMinDitherDensity,
      maxDitherDensity: settings.safeSideMaxDitherDensity,
    }
    context.save()
    context.clip(splitClips.aboveClip)
    context.fillStyle = settings.capabilitySafeSideColor
    drawPattern(safeSideSettings)
    context.restore()
    context.save()
    context.clip(splitClips.belowClip)
    context.fillStyle = fillColor
    drawPattern(settings)
    context.restore()
  } else {
    drawPattern(settings)
  }
  devPerfMeasure(`dither:${anchor}:pattern`, {
    start: patternStart,
    detail: {
      w: Math.round(rasterBounds.width),
      h: Math.round(rasterBounds.height),
      cellSize,
      split: !!splitClips,
    },
  })
  context.restore()
}

const drawFinalDitherToCanvas = (
  canvas: HTMLCanvasElement,
  rasterBounds: PatternBounds,
  minLineScreenPoints: ScreenPoint[],
  maxLineScreenPoints: ScreenPoint[],
  capabilityLineScreenPoints: ScreenPoint[],
  settings: BackgroundPatternSettings,
  fillColor: string,
  pixelRatio: number,
  thicknessAdjustment: DotThicknessAdjustment,
  restrictToBandIndex: number | null,
  globeDotExclusionMask: GlobeDotExclusionMask | null,
  capabilityLineDotExclusion: PolylineDotExclusion | null,
  clipPathData?: string,
  plotSpanX: PlotSpanX | null = null
) => {
  canvas.width = Math.max(1, Math.ceil(rasterBounds.width * pixelRatio))
  canvas.height = Math.max(1, Math.ceil(rasterBounds.height * pixelRatio))
  const context = canvas.getContext('2d')
  if (!context) return
  context.setTransform(1, 0, 0, 1, 0, 0)
  context.clearRect(0, 0, canvas.width, canvas.height)
  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
  drawFinalDitherToContext(
    context,
    rasterBounds,
    minLineScreenPoints,
    maxLineScreenPoints,
    capabilityLineScreenPoints,
    settings,
    fillColor,
    false,
    thicknessAdjustment,
    restrictToBandIndex,
    globeDotExclusionMask,
    capabilityLineDotExclusion,
    clipPathData,
    'top',
    plotSpanX
  )
}

const createCapabilityLineDotExclusion = ({
  settings,
  capabilityLineScreenPoints = [],
  additionalCapabilityLineScreenPointsList = [],
  fadingCapabilityLinePolylines = null,
}: {
  settings: BackgroundPatternSettings
  capabilityLineScreenPoints?: ScreenPoint[]
  additionalCapabilityLineScreenPointsList?: ScreenPoint[][]
  fadingCapabilityLinePolylines?: FadingCapabilityLinePolylines | null
}): PolylineDotExclusion | null => {
  if (settings.ditherPattern !== 'dots') return null
  if (!settings.capabilityLineDotExclusionEnabled) return null
  const padding = settings.capabilityLineDotExclusionPaddingPx
    + CAPABILITY_LINE_EXCLUSION_HALF_STROKE_PX
  const fullRadiusMultiplier = settings.capabilityLineDotRadiusMultiplier
  const groupFor = (
    polylines: ScreenPoint[][],
    radiusMultiplier: number,
  ): PolylineDotExclusionGroup | null => {
    const usablePolylines = polylines.filter(polyline => polyline.length >= 2)
    if (usablePolylines.length === 0) return null
    const segments = createPolylineSegments(usablePolylines)
    let maxSegmentWidthX = 0
    for (const segment of segments) {
      const width = segment.maxX - segment.minX
      if (width > maxSegmentWidthX) maxSegmentWidthX = width
    }
    return { segments, maxSegmentWidthX, padding, radiusMultiplier }
  }
  const fading = fadingCapabilityLinePolylines
  const groups: PolylineDotExclusionGroup[] = []
  const mainGroup = groupFor(
    [capabilityLineScreenPoints, ...additionalCapabilityLineScreenPointsList],
    fullRadiusMultiplier,
  )
  if (mainGroup) groups.push(mainGroup)
  if (fading && fading.strength > 0) {
    // Depth-lerped carve: the on-line multiplier eases toward 1 as strength
    // drops, so mid-fade the channel is a shallower depression, not a
    // narrower slot. Even at full strength it bottoms out at the preview
    // floor, never the main line's full depth.
    const fadingOnLineMultiplier = Math.max(
      fullRadiusMultiplier,
      PLAN_PREVIEW_ON_LINE_DOT_RADIUS_MULTIPLIER,
    )
    const fadingGroup = groupFor(
      fading.polylines,
      1 - fading.strength * (1 - fadingOnLineMultiplier),
    )
    if (fadingGroup) groups.push(fadingGroup)
  }
  if (groups.length === 0) return null
  return { groups }
}

// Bakes a canvas layer into the GPU dither renderer's inputs: the same
// per-tile radius math as drawDotDither (shade field / gradient / band radii /
// thickness adjustment / capability-line exclusion), written to a small
// Float32Array instead of Path2D arcs, plus a per-column clip mirroring the
// old fill-path clip. Returns null when the layer needs a feature only the
// canvas-2D path supports (matrix patterns, band highlight subsets, the
// capability risk split, unparseable colors) — the caller falls back.
//
// Two knowing approximations vs the 2D path (visually negligible):
// - capability-line dot exclusion tests the TILE center, not the
//   alignment-shifted dot center (≤ tileSize/2 px difference);
// - the clip boundary is sampled per column and linearly interpolated,
//   instead of the exact fill path.
// Scratch Float32Array pool for the GL bake, reused across redraws. The bake
// runs on every scroll frame; allocating fresh arrays each time (~hundreds of
// KB/frame at dpr 2) never pressured the JS heap — typed-array backing stores
// live in the renderer's native allocator, whose pools grow to absorb the
// churn and never return dirty pages to the OS. A long reading session
// accumulated hundreds of MB of native memory this way. Grow-only buffers +
// subarray views make the bake allocation-free at steady state.
const glBakeScratchBuffers = new Map<string, Float32Array>()
const getGlBakeScratch = (key: string, length: number): Float32Array => {
  const existing = glBakeScratchBuffers.get(key)
  if (existing && existing.length >= length) return existing.subarray(0, length)
  const grown = new Float32Array(length)
  glBakeScratchBuffers.set(key, grown)
  return grown.subarray(0, length)
}

export const computeDitherFieldGLLayer = (
  layer: RedAboveLossOfControlCanvasLayer,
  // Visible canvas extent in css px. The path render's bounds can run tens of
  // thousands of px past the plot (the bottom-anchored field's floor), so
  // enumerate only tiles that can actually appear — the grid stays phase-
  // anchored to the full bounds so dot positions don't shift with clamping.
  viewport: { width: number; height: number }
): DitherFieldGLLayer | null => {
  const settings = layer.settings
  if (settings.ditherPattern !== 'dots') return null
  if (settings.capabilityRiskSplitEnabled) return null
  if (layer.restrictToBandIndex !== null && layer.restrictToBandIndex !== undefined) {
    return null
  }
  const color = parseColorToRgba(layer.fillColor)
  if (!color) return null

  const anchor = layer.anchor ?? 'top'
  const rasterBounds = getRasterBounds(layer.pathRender.bounds)
  const minLineScreenPoints = layer.pathRender.minLineScreenPoints
  const maxLineScreenPoints = layer.pathRender.maxLineScreenPoints
  const capabilityLineDotExclusion = createCapabilityLineDotExclusion({
    settings,
    capabilityLineScreenPoints: layer.capabilityLineScreenPoints,
    additionalCapabilityLineScreenPointsList:
      layer.additionalCapabilityLineScreenPointsList,
    fadingCapabilityLinePolylines: layer.fadingCapabilityLinePolylines,
  })
  const thicknessAdjustment = layer.thicknessAdjustment ?? NO_DOT_THICKNESS_ADJUSTMENT
  const tileShadeField = layer.tileShadeField ?? null

  const cellSize = Math.max(1, Math.round(settings.patternScale))
  const tileSize = cellSize * 2
  const radiusCap = getMaxDotRadiusForSettings(settings, tileSize)
  const columnLayout = layer.plotSpanX
    ? createEdgeFlushColumnLayout(layer.plotSpanX, tileSize, radiusCap)
    : null
  const minLineLookup = createCurveLookup(minLineScreenPoints)
  const gradientMaxLineScreenPoints =
    settings.ditherFillToTop && anchor === 'top'
      ? maxLineScreenPoints.map((point) => ({ x: point.x, y: 0 }))
      : maxLineScreenPoints
  const maxLineLookup = createCurveLookup(gradientMaxLineScreenPoints)
  const rawMaxLineLookup = createCurveLookup(maxLineScreenPoints)

  const bandDotRadii = computeBandDotRadii(settings, tileSize, thicknessAdjustment)
  const needTileBandIndex = bandDotRadii !== null
  const bandBlendAmount = settings.bandDotRadiusBlendAmount ?? 0
  const bandCenters: number[] | null =
    bandDotRadii && bandBlendAmount > 0
      ? Array.from({ length: settings.densityBands }, (_, i) => {
          const lo = getBandLowerProgress(
            i,
            settings.densityBands,
            settings.bandThicknessMultiplier,
            settings.bandLowerProgressOverrides,
          )
          const up = getBandLowerProgress(
            i + 1,
            settings.densityBands,
            settings.bandThicknessMultiplier,
            settings.bandLowerProgressOverrides,
          )
          return (lo + up) / 2
        })
      : null
  const continuousBandRadius = (progress: number): number => {
    if (!bandDotRadii || !bandCenters) return 0
    if (bandCenters.length === 1) return bandDotRadii[0]
    if (progress <= bandCenters[0]) return bandDotRadii[0]
    if (progress >= bandCenters[bandCenters.length - 1]) {
      return bandDotRadii[bandDotRadii.length - 1]
    }
    for (let i = 0; i < bandCenters.length - 1; i++) {
      if (progress >= bandCenters[i] && progress <= bandCenters[i + 1]) {
        const span = bandCenters[i + 1] - bandCenters[i]
        const t = span > 0 ? (progress - bandCenters[i]) / span : 0
        return bandDotRadii[i] + t * (bandDotRadii[i + 1] - bandDotRadii[i])
      }
    }
    return bandDotRadii[0]
  }

  // Grid enumeration matching forEachDotTileOrigin, intersected with the
  // viewport: bounds are clamped BEFORE deriving column/row counts, but the
  // grid origin phase comes from the unclamped bounds.
  const visibleLeft = Math.max(rasterBounds.x, 0)
  const visibleRight = Math.min(rasterBounds.x + rasterBounds.width, viewport.width)
  const visibleTop = Math.max(rasterBounds.y, 0)
  const visibleBottom = Math.min(rasterBounds.y + rasterBounds.height, viewport.height)
  if (visibleRight <= visibleLeft || visibleBottom <= visibleTop) return null

  let gridFirstTileLeftX: number
  let pitch: number
  let cols: number
  if (columnLayout) {
    const firstColumnIndex = Math.max(
      0,
      Math.floor((visibleLeft - columnLayout.firstCenterX) / columnLayout.pitch),
    )
    const lastColumnIndex = Math.min(
      columnLayout.count - 1,
      Math.ceil((visibleRight - columnLayout.firstCenterX) / columnLayout.pitch),
    )
    if (lastColumnIndex < firstColumnIndex) return null
    pitch = columnLayout.pitch
    gridFirstTileLeftX =
      columnLayout.firstCenterX + firstColumnIndex * pitch - tileSize / 2
    cols = lastColumnIndex - firstColumnIndex + 1
  } else {
    const offsetX = settings.ditherPatternOffsetX
    pitch = tileSize
    gridFirstTileLeftX =
      Math.floor((visibleLeft - offsetX) / tileSize) * tileSize + offsetX
    cols = Math.max(
      0,
      Math.ceil((visibleRight - gridFirstTileLeftX) / tileSize),
    )
  }
  const gridStartY = Math.floor(visibleTop / tileSize) * tileSize
  const rows = Math.max(
    0,
    Math.ceil((visibleBottom - gridStartY) / tileSize),
  )
  if (cols <= 0 || rows <= 0) return null

  // Per-column vertical extent of the capability line(s), padded by the
  // exclusion influence: only tiles inside this band can have their radius
  // scaled, so everything else skips the per-dot distance-to-segment test
  // (which otherwise dominates the bake).
  let exclusionBandByColumn: Float32Array | null = null
  if (capabilityLineDotExclusion) {
    // Every column is initialized in the loop below — no zeroing needed.
    exclusionBandByColumn = getGlBakeScratch(`exclusion:${anchor}`, cols * 2)
    for (let col = 0; col < cols; col += 1) {
      exclusionBandByColumn[col * 2] = Infinity
      exclusionBandByColumn[col * 2 + 1] = -Infinity
    }
    for (const group of capabilityLineDotExclusion.groups) {
      const influence = radiusCap + group.padding + tileSize
      for (const segment of group.segments) {
        const firstColumn = Math.max(
          0,
          Math.floor((segment.minX - influence - gridFirstTileLeftX) / pitch),
        )
        const lastColumn = Math.min(
          cols - 1,
          Math.ceil((segment.maxX + influence - gridFirstTileLeftX) / pitch),
        )
        for (let col = firstColumn; col <= lastColumn; col += 1) {
          const bandMin = segment.minY - influence
          const bandMax = segment.maxY + influence
          if (bandMin < exclusionBandByColumn[col * 2]) {
            exclusionBandByColumn[col * 2] = bandMin
          }
          if (bandMax > exclusionBandByColumn[col * 2 + 1]) {
            exclusionBandByColumn[col * 2 + 1] = bandMax
          }
        }
      }
    }
  }

  const tileLoopStart = performance.now()
  // fill(0) is load-bearing: the bake loops `continue` past out-of-band tiles
  // and rely on unwritten entries reading as radius 0 (no dot).
  const radii = getGlBakeScratch(`radii:${anchor}`, cols * rows)
  radii.fill(0)
  const shadeModel = layer.shadeModel ?? null
  if (shadeModel) {
    // Fast path: the field is separable, so resolve the per-column and
    // per-row parameters once, bake the confidence→density stops into a LUT,
    // and keep the per-tile inner loop to a few flops. This is what makes the
    // bake ~1ms instead of tens of ms of per-tile closure calls.
    const LUT_SIZE = 512
    const sortedStops = [...shadeModel.stops].sort((a, b) => a.confidence - b.confidence)
    const densityLut = getGlBakeScratch(`lut:${anchor}`, LUT_SIZE)
    for (let i = 0; i < LUT_SIZE; i += 1) {
      densityLut[i] = densityFromConfidenceStops(i / (LUT_SIZE - 1), sortedStops)
    }
    const densityToRadius = (density: number): number => {
      if (density <= 0) return 0
      const naturalRadius = tileSize * Math.sqrt(density / Math.PI)
      return applyDotThicknessAdjustment(naturalRadius, radiusCap, thicknessAdjustment)
    }
    if (shadeModel.kind === 'progressStops') {
      // Per column: strip geometry; per tile: linear progress in y → LUT.
      for (let col = 0; col < cols; col += 1) {
        const tileCenterX = gridFirstTileLeftX + col * pitch + tileSize / 2
        const minY = interpolateCurveLookupYAtX(tileCenterX, minLineLookup)
        const maxY = interpolateCurveLookupYAtX(tileCenterX, maxLineLookup)
        if (minY === null || maxY === null) continue
        const stripHeight = anchor === 'bottom' ? maxY - minY : minY - maxY
        const invStripHeight = stripHeight > 0 ? 1 / stripHeight : 0
        for (let row = 0; row < rows; row += 1) {
          const tileCenterY = gridStartY + row * tileSize + tileSize / 2
          // Wrong side of the min line → outside the field.
          if (anchor === 'bottom' ? tileCenterY < minY : tileCenterY > minY) continue
          const rawProgress = stripHeight <= 0
            ? 1
            : (anchor === 'bottom' ? tileCenterY - minY : minY - tileCenterY)
              * invStripHeight
          const progress = rawProgress < 0 ? 0 : rawProgress > 1 ? 1 : rawProgress
          const radius = densityToRadius(
            densityLut[(progress * (LUT_SIZE - 1)) | 0]
          )
          if (radius > 0) radii[row * cols + col] = radius
        }
      }
    } else {
      // trailingCdf: per-column year params, per-row capability params,
      // bilinear CDF combine per tile.
      const evaluator = createTrailingActorRiskConfidenceEvaluator(
        shadeModel.survivalYears
      )
      const yearParamsPerColumn = new Array<TrailingActorRiskYearParams>(cols)
      for (let col = 0; col < cols; col += 1) {
        const tileCenterX = gridFirstTileLeftX + col * pitch + tileSize / 2
        yearParamsPerColumn[col] = evaluator.yearParamsFor(
          shadeModel.yearForScreenX(tileCenterX)
        )
      }
      const capParamsPerRow = new Array<TrailingActorRiskCapParams>(rows)
      for (let row = 0; row < rows; row += 1) {
        const tileCenterY = gridStartY + row * tileSize + tileSize / 2
        capParamsPerRow[row] = evaluator.capParamsFor(
          shadeModel.valueForY(tileCenterY)
        )
      }
      for (let row = 0; row < rows; row += 1) {
        const capParams = capParamsPerRow[row]
        for (let col = 0; col < cols; col += 1) {
          const confidence = evaluator.confidenceAt(
            yearParamsPerColumn[col],
            capParams
          )
          const radius = densityToRadius(
            densityLut[(clamp01(confidence) * (LUT_SIZE - 1)) | 0]
          )
          if (radius > 0) radii[row * cols + col] = radius
        }
      }
    }
    // Capability-line exclusion pass over the (few) tiles near the line.
    if (exclusionBandByColumn && capabilityLineDotExclusion) {
      for (let col = 0; col < cols; col += 1) {
        const bandMin = exclusionBandByColumn[col * 2]
        const bandMax = exclusionBandByColumn[col * 2 + 1]
        if (bandMin > bandMax) continue
        const tileCenterX = gridFirstTileLeftX + col * pitch + tileSize / 2
        for (let row = 0; row < rows; row += 1) {
          const radius = radii[row * cols + col]
          if (radius <= 0) continue
          const tileCenterY = gridStartY + row * tileSize + tileSize / 2
          if (tileCenterY < bandMin || tileCenterY > bandMax) continue
          const multiplier = getPolylineDotRadiusMultiplier(
            { x: tileCenterX, y: tileCenterY },
            radius,
            capabilityLineDotExclusion
          )
          radii[row * cols + col] = multiplier > 0 ? radius * multiplier : 0
        }
      }
    }
  } else {
  for (let row = 0; row < rows; row += 1) {
    const y = gridStartY + row * tileSize
    for (let col = 0; col < cols; col += 1) {
      const x = gridFirstTileLeftX + col * pitch
      const tileCenterX = x + tileSize / 2
      const tileCenterY = y + tileSize / 2
      let radius: number
      if (tileShadeField) {
        const shade = tileShadeField(tileCenterX, tileCenterY)
        if (shade <= 0) continue
        const naturalRadius = tileSize * Math.sqrt(clamp01(shade) / Math.PI)
        radius = applyDotThicknessAdjustment(naturalRadius, radiusCap, thicknessAdjustment)
      } else if (bandDotRadii) {
        const tileBandIndex = needTileBandIndex
          ? getBandIndexAtTileCenterFromLookups(
              { x: tileCenterX, y: tileCenterY },
              minLineLookup,
              maxLineLookup,
              settings,
              anchor
            )
          : null
        if (tileBandIndex === null) continue
        const discreteRadius = bandDotRadii[tileBandIndex]
        if (bandCenters && bandBlendAmount > 0) {
          const rawProgress = getRawProgressAtPointFromLookups(
            { x: tileCenterX, y: tileCenterY },
            minLineLookup,
            maxLineLookup,
            anchor,
          )
          if (rawProgress !== null) {
            const noisyProgress = clamp01(
              rawProgress
                + sampleBandBoundaryProgressOffset(
                  { x: tileCenterX, y: tileCenterY },
                  settings,
                ),
            )
            const continuous = continuousBandRadius(noisyProgress)
            radius = discreteRadius + bandBlendAmount * (continuous - discreteRadius)
          } else {
            radius = discreteRadius
          }
        } else {
          radius = discreteRadius
        }
      } else {
        const baseDensity = getTileDensityFromLookups(
          x,
          y,
          tileSize,
          minLineLookup,
          maxLineLookup,
          settings,
          anchor
        )
        if (baseDensity <= 0) continue
        const naturalRadius = tileSize * Math.sqrt(baseDensity / Math.PI)
        radius = applyDotThicknessAdjustment(naturalRadius, radiusCap, thicknessAdjustment)
      }
      if (radius <= 0) continue
      if (
        exclusionBandByColumn
        && tileCenterY >= exclusionBandByColumn[col * 2]
        && tileCenterY <= exclusionBandByColumn[col * 2 + 1]
      ) {
        const lineRadiusMultiplier = getPolylineDotRadiusMultiplier(
          { x: tileCenterX, y: tileCenterY },
          radius,
          capabilityLineDotExclusion
        )
        if (lineRadiusMultiplier <= 0) continue
        radius *= lineRadiusMultiplier
      }
      radii[row * cols + col] = radius
    }
  }
  }

  devPerfMeasure(`dither:gl-bake:${anchor}:tiles`, {
    start: tileLoopStart,
    detail: { cols, rows, shade: !!tileShadeField, banded: !!bandDotRadii },
  })
  // Per-column vertical clip standing in for the old fill-path clip: one real
  // boundary per anchor (top field clips at the lower/min line, bottom field
  // at the upper/frontier line), the other side left open.
  // Every column is written below (both branches) — no zeroing needed.
  const clipBounds = getGlBakeScratch(`clip:${anchor}`, cols * 2)
  for (let col = 0; col < cols; col += 1) {
    const centerX = gridFirstTileLeftX + col * pitch + tileSize / 2
    const yA = interpolateCurveLookupYAtX(centerX, minLineLookup)
    const yB = interpolateCurveLookupYAtX(centerX, rawMaxLineLookup)
    if (yA === null || yB === null) {
      // Outside the band's x-range: cut the column entirely.
      clipBounds[col * 2] = 1e9
      clipBounds[col * 2 + 1] = -1e9
      continue
    }
    const upperY = Math.min(yA, yB)
    const lowerY = Math.max(yA, yB)
    if (anchor === 'bottom') {
      // Floor-anchored field: cut at the frontier (upper) edge; the raster
      // bounds already end at the plot floor.
      clipBounds[col * 2] = upperY
      clipBounds[col * 2 + 1] = 1e9
    } else {
      // Top-anchored field: cut at the min (lower) line only — the old fill
      // path leaves the top open (density above the max line saturates).
      clipBounds[col * 2] = -1e9
      clipBounds[col * 2 + 1] = lowerY
    }
  }

  return {
    radii,
    cols,
    rows,
    gridFirstTileLeftX,
    pitch,
    gridStartY,
    tileSize,
    // Edge-flush layout: align across the plot span (flush exact at the plot
    // edges) and slide within the max-dot extent; legacy grid: align across
    // the raster bounds and slide within the tile. Mirrors drawDotDither.
    rasterOriginX: layer.plotSpanX ? layer.plotSpanX.left : rasterBounds.x,
    fieldWidth: layer.plotSpanX
      ? layer.plotSpanX.right - layer.plotSpanX.left
      : rasterBounds.width,
    edgeFlushMaxDotRadius: columnLayout ? radiusCap : null,
    clipBounds,
    color,
    opacity: layer.opacity,
    dotShape: settings.dotShape === 'square' ? 'square' : 'circle',
  }
}

export const drawRedAboveLossOfControlDitherCanvasLayer = (
  context: CanvasRenderingContext2D,
  layer: RedAboveLossOfControlCanvasLayer
) => {
  if (layer.opacity <= 0) return
  // The path render's bounds can run hundreds of thousands of px past the
  // plot (a bottom-anchored field whose far edge maps way off a small-yMax
  // frame — Plan S's flat line hits this). Enumerating that many tile rows
  // takes ~500ms per frame, so intersect with the canvas extent like the GL
  // bake does (see computeDitherFieldGLLayer's viewport clamp). The tile
  // grid snaps to ABSOLUTE multiples of tileSize, so clamping the bounds
  // only skips off-canvas tiles — no dot moves.
  const transform = context.getTransform()
  const cssWidth =
    transform.a > 0 ? context.canvas.width / transform.a : Number.POSITIVE_INFINITY
  const cssHeight =
    transform.d > 0 ? context.canvas.height / transform.d : Number.POSITIVE_INFINITY
  const clampTile = Math.max(1, Math.round(layer.settings.patternScale)) * 2
  const unclampedRasterBounds = getRasterBounds(layer.pathRender.bounds)
  const rasterBounds = getRasterBounds(
    intersectBoundsWithViewport(
      layer.pathRender.bounds,
      cssWidth,
      cssHeight,
      clampTile,
    )
  )
  const capabilityLineDotExclusion = createCapabilityLineDotExclusion({
    settings: layer.settings,
    capabilityLineScreenPoints: layer.capabilityLineScreenPoints,
    additionalCapabilityLineScreenPointsList:
      layer.additionalCapabilityLineScreenPointsList,
    fadingCapabilityLinePolylines: layer.fadingCapabilityLinePolylines,
  })

  context.save()
  context.globalAlpha = context.globalAlpha * layer.opacity
  drawFinalDitherToContext(
    context,
    rasterBounds,
    layer.pathRender.minLineScreenPoints,
    layer.pathRender.maxLineScreenPoints,
    layer.capabilityLineScreenPoints ?? [],
    layer.settings,
    layer.fillColor,
    true,
    layer.thicknessAdjustment ?? NO_DOT_THICKNESS_ADJUSTMENT,
    layer.restrictToBandIndex ?? null,
    null,
    capabilityLineDotExclusion,
    layer.clipPathData ?? layer.pathRender.fillPathData,
    layer.anchor ?? 'top',
    layer.plotSpanX ?? null,
    layer.tileShadeField ?? null,
    { x: unclampedRasterBounds.x, width: unclampedRasterBounds.width }
  )
  context.restore()
}

const FinalDitherCanvas = ({
  pathRender,
  settings,
  fillColor,
  clipPathData,
  capabilityLineScreenPoints = [],
  additionalCapabilityLineScreenPointsList = [],
  fadingCapabilityLinePolylines = null,
  thicknessAdjustment = NO_DOT_THICKNESS_ADJUSTMENT,
  restrictToBandIndex = null,
  featherStdDev = 0,
  fadeInMs = 0,
  publishAboveGlobe = false,
  plotSpanX = null,
}: {
  pathRender: PathRender
  settings: BackgroundPatternSettings
  fillColor: string
  clipPathData?: string
  capabilityLineScreenPoints?: ScreenPoint[]
  additionalCapabilityLineScreenPointsList?: ScreenPoint[][]
  fadingCapabilityLinePolylines?: FadingCapabilityLinePolylines | null
  thicknessAdjustment?: DotThicknessAdjustment
  // Plot-area horizontal extent for the edge-flush exact-fit dot columns.
  // Must match the static canvas dither's value so the hovered-band
  // highlight tiles stay aligned with the base pattern's tiles.
  plotSpanX?: PlotSpanX | null
  // Non-null: paint only matching-band tiles (tile-perfect highlight).
  restrictToBandIndex?: number | null
  // CSS gaussian blur softening the tile-quantized highlight edges.
  featherStdDev?: number
  // One-shot fade-in (ms) when the image first appears; 0 disables.
  fadeInMs?: number
  // True: publish to the above-globe layer so it paints over the globe.
  publishAboveGlobe?: boolean
}) => {
  const { setHighlight } = useBandHudOverlayPublisher()
  const placeholderRef = useRef<SVGRectElement | null>(null)
  const imageHrefRef = useRef<string | null>(null)
  const [imageHref, setImageHref] = useState<string | null>(null)
  const [fadeInReached, setFadeInReached] = useState(false)
  const [globeDotExclusionMask, setGlobeDotExclusionMask] =
    useState<GlobeDotExclusionMask | null>(null)
  const globeDotExclusionMaskRef = useRef<GlobeDotExclusionMask | null>(null)
  const globeDotExclusionSignatureRef = useRef('')
  const rasterBounds = getRasterBounds(pathRender.bounds)
  const { x: rasterX, y: rasterY } = rasterBounds
  const { width: rasterWidth, height: rasterHeight } = rasterBounds
  const minLineScreenPoints = pathRender.minLineScreenPoints
  const maxLineScreenPoints = pathRender.maxLineScreenPoints
  const effectiveClipPathData = clipPathData ?? pathRender.fillPathData
  const dotRadiusMultiplier = thicknessAdjustment.dotRadiusMultiplier
  const dotRadiusAdditivePx = thicknessAdjustment.dotRadiusAdditivePx
  const shouldFilterGlobeDots =
    settings.ditherPattern === 'dots' && settings.globeDotExclusionEnabled
  const globeDotExclusionPaddingPx = settings.globeDotExclusionPaddingPx
  const capabilityLineDotExclusion = useMemo<PolylineDotExclusion | null>(() => {
    return createCapabilityLineDotExclusion({
      settings,
      capabilityLineScreenPoints,
      additionalCapabilityLineScreenPointsList,
      fadingCapabilityLinePolylines,
    })
  }, [
    settings,
    capabilityLineScreenPoints,
    additionalCapabilityLineScreenPointsList,
    fadingCapabilityLinePolylines,
  ])

  // Publish highlight to the above-globe layer; raster bounds are
  // already graph-local, so no getBoundingClientRect is needed.
  const highlightOpacity = fadeInMs && !fadeInReached ? 0 : settings.opacity
  useEffect(() => {
    if (!publishAboveGlobe) return
    if (!imageHref) {
      setHighlight(null)
      return
    }
    setHighlight({
      imageHref,
      left: rasterX,
      top: rasterY,
      width: rasterWidth,
      height: rasterHeight,
      opacity: highlightOpacity,
      featherBlurPx: featherStdDev > 0 ? featherStdDev : 0,
      fadeInMs: fadeInMs ?? 0,
    })
    return () => setHighlight(null)
  }, [
    publishAboveGlobe,
    imageHref,
    rasterX,
    rasterY,
    rasterWidth,
    rasterHeight,
    highlightOpacity,
    featherStdDev,
    fadeInMs,
    setHighlight,
  ])

  useEffect(() => {
    if (!shouldFilterGlobeDots) {
      globeDotExclusionSignatureRef.current = 'disabled'
      if (globeDotExclusionMaskRef.current !== null) {
        globeDotExclusionMaskRef.current = null
        setGlobeDotExclusionMask(null)
      }
      return
    }
    if (typeof window === 'undefined') return
    let animationFrameId: number | null = null
    const measure = () => {
      const placeholder = placeholderRef.current
      const snapshot = placeholder
        ? getGlobeDotExclusionMaskSnapshot(
            placeholder,
            {
              x: rasterX,
              y: rasterY,
              width: rasterWidth,
              height: rasterHeight,
            },
            globeDotExclusionPaddingPx
          )
        : { mask: null, signature: 'no-dither-canvas' }
      if (snapshot.signature !== globeDotExclusionSignatureRef.current) {
        globeDotExclusionSignatureRef.current = snapshot.signature
        if (globeDotExclusionMaskRef.current !== snapshot.mask) {
          globeDotExclusionMaskRef.current = snapshot.mask
          setGlobeDotExclusionMask(snapshot.mask)
        }
      }
      animationFrameId = window.requestAnimationFrame(measure)
    }
    measure()
    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId)
      }
    }
  }, [
    shouldFilterGlobeDots,
    globeDotExclusionPaddingPx,
    rasterHeight,
    rasterWidth,
    rasterX,
    rasterY,
  ])

  useEffect(() => () => {
    if (imageHrefRef.current) {
      URL.revokeObjectURL(imageHrefRef.current)
      imageHrefRef.current = null
    }
  }, [])

  // Two rAFs, not one: the reused <img> never remounts, so one rAF
  // collapses into a single style recalc and the transition is skipped.
  useEffect(() => {
    if (!fadeInMs) return
    if (!imageHref) return
    let secondFrameId: number | null = null
    const firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => setFadeInReached(true))
    })
    return () => {
      window.cancelAnimationFrame(firstFrameId)
      if (secondFrameId !== null) window.cancelAnimationFrame(secondFrameId)
    }
  }, [fadeInMs, imageHref])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const canvas = document.createElement('canvas')
    const pixelRatio = 1
    drawFinalDitherToCanvas(
      canvas,
      {
        x: rasterX,
        y: rasterY,
        width: rasterWidth,
        height: rasterHeight,
      },
      minLineScreenPoints,
      maxLineScreenPoints,
      capabilityLineScreenPoints,
      settings,
      fillColor,
      pixelRatio,
      { dotRadiusMultiplier, dotRadiusAdditivePx },
      restrictToBandIndex,
      globeDotExclusionMask,
      capabilityLineDotExclusion,
      effectiveClipPathData,
      plotSpanX
    )
    let cancelled = false
    canvas.toBlob(blob => {
      if (!blob) return
      const nextImageHref = URL.createObjectURL(blob)
      if (cancelled) {
        URL.revokeObjectURL(nextImageHref)
        return
      }
      const previousImageHref = imageHrefRef.current
      imageHrefRef.current = nextImageHref
      if (fadeInMs) setFadeInReached(false)
      setImageHref(nextImageHref)
      if (previousImageHref) URL.revokeObjectURL(previousImageHref)
    }, 'image/png')
    return () => {
      cancelled = true
    }
  }, [
    dotRadiusMultiplier,
    dotRadiusAdditivePx,
    fillColor,
    rasterHeight,
    rasterWidth,
    rasterX,
    rasterY,
    settings,
    minLineScreenPoints,
    maxLineScreenPoints,
    capabilityLineScreenPoints,
    restrictToBandIndex,
    globeDotExclusionMask,
    capabilityLineDotExclusion,
    effectiveClipPathData,
    fadeInMs,
    plotSpanX,
  ])

  // Invisible rect = DOM anchor the globe-dot-exclusion mask sampling
  // needs to map globe canvases into raster-local pixels. Paints nothing.
  return (
    <rect
      ref={placeholderRef}
      x={rasterBounds.x}
      y={rasterBounds.y}
      width={rasterBounds.width}
      height={rasterBounds.height}
      fill="transparent"
      opacity={0}
      pointerEvents="none"
    />
  )
}

// Hovered-band danger label config; rendered by AboveGlobeBandHudOverlay.
const BAND_DANGER_LABEL_FONT_SIZE_PX = 10
const BAND_DANGER_LABEL_LEFT_PADDING_PX = 10
const BAND_DANGER_LABEL_TEXT_COLOR = '#000000'
const BAND_DANGER_LABEL_BACKGROUND_COLOR = 'var(--playbook-epilogue-bg, #fffff8)'
// Gap below the top edge so a clamped top-band label isn't flush.
const BAND_DANGER_LABEL_TOP_PADDING_PX = 6
const BAND_DANGER_LABEL_FADE_MS = 150
const BAND_HIGHLIGHT_FADE_MS = 150

// Danger label. anchor is graph-local, so it maps straight to
// left/top with no getBoundingClientRect.
export const BandDangerLabel = ({
  anchor,
  label,
  opacity,
}: {
  anchor: { x: number; y: number }
  label: string
  opacity: number
}) => {
  const [appeared, setAppeared] = useState(false)
  const previousBandKeyRef = useRef<string | null>(null)

  // Reset to hidden during render (not an effect) so the label hides
  // in the same frame it repositions, avoiding a blink on band change.
  const bandKey = `${anchor.y}|${label}`
  if (previousBandKeyRef.current !== bandKey) {
    previousBandKeyRef.current = bandKey
    setAppeared(false)
  }

  // Flip to target opacity a frame later so the CSS transition runs.
  useEffect(() => {
    const animationFrameId = window.requestAnimationFrame(() => setAppeared(true))
    return () => window.cancelAnimationFrame(animationFrameId)
  }, [bandKey])

  return (
    <div
      aria-hidden="true"
      className="font-mono"
      style={{
        position: 'absolute',
        left: anchor.x + BAND_DANGER_LABEL_LEFT_PADDING_PX,
        top: anchor.y,
        transform: 'translateY(-50%)',
        fontSize: BAND_DANGER_LABEL_FONT_SIZE_PX,
        lineHeight: 1,
        color: BAND_DANGER_LABEL_TEXT_COLOR,
        opacity: appeared ? opacity : 0,
        transition: `opacity ${BAND_DANGER_LABEL_FADE_MS}ms ease`,
        backgroundColor: BAND_DANGER_LABEL_BACKGROUND_COLOR,
        padding: '0px 3px',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        textShadow: getLabelStroke('#ffffff'),
      }}
    >
      {label}
    </div>
  )
}

const renderMaskRect = (
  bounds: PatternBounds,
  additionalProps: { fill?: string; filter?: string }
) => (
  <rect
    x={bounds.x}
    y={bounds.y}
    width={bounds.width}
    height={bounds.height}
    {...additionalProps}
  />
)

export const createRedAboveLossOfControlPathRender = ({
  show,
  lossOfControlMinPoints,
  lossOfControlMaxPoints,
  effectiveCappedTimestamp,
  xForTimestamp,
  yForValue,
  anchor = 'top',
  floorY,
}: {
  show: boolean
  lossOfControlMinPoints: GraphPoint[]
  lossOfControlMaxPoints: GraphPoint[]
  effectiveCappedTimestamp: number
  xForTimestamp: (timestamp: number) => number
  yForValue: (value: number) => number
  // 'bottom' anchors the fill to the plot floor (requires floorY).
  anchor?: DitherAnchor
  floorY?: number
}): RedAboveLossOfControlPathRender | null => {
  if (!show) return null
  const visibleMinPoints = lossOfControlMinPoints.filter(graphPoint =>
    graphPoint.timestamp <= effectiveCappedTimestamp
  )
  const visibleMaxPoints = lossOfControlMaxPoints.filter(graphPoint =>
    graphPoint.timestamp <= effectiveCappedTimestamp
  )
  if (visibleMinPoints.length < 2) return null
  if (visibleMaxPoints.length < 2) return null

  if (anchor === 'bottom') {
    return createBottomAnchoredPathRender({
      visibleMinPoints,
      visibleMaxPoints,
      effectiveCappedTimestamp,
      xForTimestamp,
      yForValue,
      floorY: floorY ?? 0,
    })
  }

  let minX = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let lowestMinY = Number.NEGATIVE_INFINITY
  const minLinePathParts: string[] = []
  const minLineScreenPoints: ScreenPoint[] = []
  for (let pointIndex = 0; pointIndex < visibleMinPoints.length; pointIndex += 1) {
    const boundaryPoint = visibleMinPoints[pointIndex]
    const x = xForTimestamp(boundaryPoint.timestamp)
    const y = yForValue(boundaryPoint.value)
    minLinePathParts.push(`${pointIndex === 0 ? 'M' : 'L'} ${x} ${y}`)
    minLineScreenPoints.push({ x, y })
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y > lowestMinY) lowestMinY = y
  }
  // Clamp the progress-1 edge to the plot top (y=0, where the fill closes),
  // mirroring the bottom-anchored field's clamp below. The shade at a pixel is
  // its position between the min line (0) and this line (1); a plan whose max
  // boundary plateaus decades above the y-ceiling (B/C: 30-40k vs a 144 frame)
  // otherwise puts every visible pixel at ~0 progress and the red field fades
  // to nothing mid-axis. Clamped, the gradient normalizes over the visible
  // strip and reaches full red at the frame top — except where the min line
  // itself nears the top: there the visible strip collapses and a hard clamp
  // would compress the whole ramp into a few px (a sharp red sliver). The ramp
  // floor keeps the effective max edge at least a fade's worth above the min
  // line (off-frame if needed), so the red instead fades out smoothly as the
  // band exits the frame. The floor is slope-aware: MIN_CLAMPED_GRADIENT_RAMP_PX
  // is the fade width PERPENDICULAR to the min line, so where the boundary
  // climbs steeply (B/C near the frame top) the vertical ramp stretches by
  // √(1+m²) and the fade stays wide instead of thinning into a sharp diagonal.
  const minLineSlopeMagnitudeAt = (pointIndex: number): number => {
    const lastMinLineIndex = minLineScreenPoints.length - 1
    const previousPoint =
      minLineScreenPoints[Math.min(lastMinLineIndex, Math.max(0, pointIndex - 1))]
    const nextPoint =
      minLineScreenPoints[Math.min(lastMinLineIndex, pointIndex + 1)]
    const deltaX = nextPoint.x - previousPoint.x
    if (deltaX <= 0) return 0
    return Math.abs((nextPoint.y - previousPoint.y) / deltaX)
  }
  const maxLineScreenPoints: ScreenPoint[] = visibleMaxPoints.map(
    (boundaryPoint, pointIndex) => {
      const minLineY = minLineScreenPoints[pointIndex]?.y ?? 0
      const slopeMagnitude = minLineSlopeMagnitudeAt(pointIndex)
      const rampPx = MIN_CLAMPED_GRADIENT_RAMP_PX * Math.min(
        CLAMPED_GRADIENT_RAMP_SLOPE_FACTOR_CAP,
        Math.sqrt(1 + slopeMagnitude * slopeMagnitude),
      )
      return {
        x: xForTimestamp(boundaryPoint.timestamp),
        y: Math.max(
          yForValue(boundaryPoint.value),
          Math.min(0, minLineY - rampPx),
        ),
      }
    }
  )
  const graphRightX = xForTimestamp(effectiveCappedTimestamp)
  const fillMaxX = Math.max(maxX, graphRightX)
  if (fillMaxX > maxX) {
    const lastMinScreenPoint = minLineScreenPoints[minLineScreenPoints.length - 1]
    minLinePathParts.push(`L ${fillMaxX} ${lastMinScreenPoint.y}`)
    minLineScreenPoints.push({ x: fillMaxX, y: lastMinScreenPoint.y })
    const lastMaxScreenPoint = maxLineScreenPoints[maxLineScreenPoints.length - 1]
    maxLineScreenPoints.push({ x: fillMaxX, y: lastMaxScreenPoint.y })
    maxX = fillMaxX
  }
  const fillPathData =
    `${minLinePathParts.join(' ')} L ${maxX} 0 L ${minX} 0 Z`
  // PATH_BOUNDS_PADDING gives filters / dither tiles room to spread.
  const bounds: PatternBounds = {
    x: minX - PATH_BOUNDS_PADDING,
    y: -PATH_BOUNDS_PADDING,
    width: (maxX - minX) + PATH_BOUNDS_PADDING * 2,
    height: lowestMinY + PATH_BOUNDS_PADDING * 2,
  }
  return { fillPathData, bounds, minLineScreenPoints, maxLineScreenPoints }
}

// Floor-anchored mirror: minLine = upper edge (progress 0), maxLine
// = lower edge (progress 1); fill traces upper edge, closes to floorY.
const createBottomAnchoredPathRender = ({
  visibleMinPoints,
  visibleMaxPoints,
  effectiveCappedTimestamp,
  xForTimestamp,
  yForValue,
  floorY,
}: {
  visibleMinPoints: GraphPoint[]
  visibleMaxPoints: GraphPoint[]
  effectiveCappedTimestamp: number
  xForTimestamp: (timestamp: number) => number
  yForValue: (value: number) => number
  floorY: number
}): RedAboveLossOfControlPathRender | null => {
  const projectSeries = (points: GraphPoint[]): ScreenPoint[] =>
    points.map(point => ({
      x: xForTimestamp(point.timestamp),
      y: yForValue(point.value),
    }))
  // Pick upper edge by avg screen-y, ignoring caller's min/max labeling.
  const seriesA = projectSeries(visibleMinPoints)
  const seriesB = projectSeries(visibleMaxPoints)
  const avgY = (points: ScreenPoint[]): number =>
    points.reduce((sum, point) => sum + point.y, 0) / points.length
  const rawUpperEdge = avgY(seriesA) <= avgY(seriesB) ? seriesA : seriesB
  const lowerEdge = rawUpperEdge === seriesA ? seriesB : seriesA
  // Clamp the upper edge to the plot top (y=0, where the top-anchored red
  // field closes its fill). The full-column trailing-actor band's top contour
  // is THRESHOLDS[last] (100000×), which on a frame maxing at tens-to-hundreds
  // maps tens of thousands of px ABOVE the plot; left unclamped the fill region
  // is ~250k px tall and the dither tiles spread so thin that none land in the
  // visible plot — the black field renders invisibly. Clamping makes the region
  // the visible column, so the CDF shade fills it as intended.
  const upperEdge = rawUpperEdge.map(point => ({
    ...point,
    y: Math.max(point.y, 0),
  }))

  let minX = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let highestY = Number.POSITIVE_INFINITY // smallest screen-y in band
  for (const point of [...upperEdge, ...lowerEdge]) {
    if (point.x < minX) minX = point.x
    if (point.x > maxX) maxX = point.x
    if (point.y < highestY) highestY = point.y
  }

  const minLineScreenPoints = upperEdge.map(point => ({ ...point }))
  const maxLineScreenPoints = lowerEdge.map(point => ({ ...point }))

  const graphRightX = xForTimestamp(effectiveCappedTimestamp)
  const fillMaxX = Math.max(maxX, graphRightX)
  if (fillMaxX > maxX) {
    const lastUpper = minLineScreenPoints[minLineScreenPoints.length - 1]
    minLineScreenPoints.push({ x: fillMaxX, y: lastUpper.y })
    const lastLower = maxLineScreenPoints[maxLineScreenPoints.length - 1]
    maxLineScreenPoints.push({ x: fillMaxX, y: lastLower.y })
    maxX = fillMaxX
  }

  const upperEdgePathParts = minLineScreenPoints.map((point, index) =>
    `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  )
  const fillPathData =
    `${upperEdgePathParts.join(' ')} L ${maxX} ${floorY} L ${minX} ${floorY} Z`
  const bounds: PatternBounds = {
    x: minX - PATH_BOUNDS_PADDING,
    y: highestY - PATH_BOUNDS_PADDING,
    width: (maxX - minX) + PATH_BOUNDS_PADDING * 2,
    height: (floorY - highestY) + PATH_BOUNDS_PADDING * 2,
  }
  return { fillPathData, bounds, minLineScreenPoints, maxLineScreenPoints }
}

export const createRedAboveLossOfControlDitherCanvasLayer = ({
  pathRender,
  settings,
  faded = false,
  capabilityLineScreenPoints,
  additionalCapabilityLineScreenPointsList = [],
  fadingCapabilityLinePolylines = null,
  anchor = 'top',
  plotSpanX,
  tileShadeField = null,
  shadeModel = null,
}: {
  pathRender: RedAboveLossOfControlPathRender | null
  settings: BackgroundPatternSettings
  faded?: boolean
  capabilityLineScreenPoints?: ScreenPoint[]
  additionalCapabilityLineScreenPointsList?: ScreenPoint[][]
  fadingCapabilityLinePolylines?: FadingCapabilityLinePolylines | null
  anchor?: DitherAnchor
  plotSpanX?: PlotSpanX
  tileShadeField?: TileShadeField | null
  shadeModel?: DitherShadeModel | null
}): RedAboveLossOfControlCanvasLayer | null => {
  if (!pathRender) return null
  if (!settings.enabled) return null
  if (settings.ditherPattern === 'none') return null
  const densityBandsHoverOnly =
    settings.densityBandsHoverOnly && settings.densityBands >= 2
  const mainPatternSettings: BackgroundPatternSettings = densityBandsHoverOnly
    ? { ...settings, densityBands: 0 }
    : settings
  return {
    pathRender,
    settings: mainPatternSettings,
    fillColor: settings.color,
    opacity: faded ? 0 : settings.opacity,
    capabilityLineScreenPoints,
    additionalCapabilityLineScreenPointsList,
    fadingCapabilityLinePolylines,
    anchor,
    plotSpanX,
    tileShadeField,
    shadeModel,
  }
}

// The epilogue fades this overlay away with the rest of the HUD.
const OVERLAY_FADE_TRANSITION_MS = EPILOGUE_HUD_FADE_OUT_MS

export const RedAboveLossOfControlOverlay = ({
  show,
  lossOfControlMinPoints,
  lossOfControlMaxPoints,
  effectiveCappedTimestamp,
  xForTimestamp,
  yForValue,
  valueForY,
  onRiskHoverChange,
  faded = false,
  interactiveOnly = false,
  capabilityLineScreenPoints,
  additionalCapabilityLinePolylines = [],
  fadingCapabilityLinePolylines = null,
  plotSpanX = null,
}: {
  show: boolean
  lossOfControlMinPoints: GraphPoint[]
  lossOfControlMaxPoints: GraphPoint[]
  effectiveCappedTimestamp: number
  xForTimestamp: (timestamp: number) => number
  yForValue: (value: number) => number
  valueForY: (screenY: number) => number
  onRiskHoverChange?: (riskPercent: number | null) => void
  faded?: boolean
  // True: skip the static fill (ExtrapolatedGraphRenderer's canvas
  // layer draws it); render only the interactive bits.
  interactiveOnly?: boolean
  capabilityLineScreenPoints?: ScreenPoint[]
  additionalCapabilityLinePolylines?: ScreenPoint[][]
  fadingCapabilityLinePolylines?: FadingCapabilityLinePolylines | null
  // Plot-area horizontal extent for the edge-flush exact-fit dot columns —
  // must match the value the static canvas dither layer was created with so
  // the hovered-band highlight tiles align with it. See PlotSpanX.
  plotSpanX?: PlotSpanX | null
}) => {
  const reactInstanceId = useId()
  const { setLabel } = useBandHudOverlayPublisher()
  const [hoveredOverlayPoint, setHoveredOverlayPoint] =
    useState<ScreenPoint | null>(null)
  const idSuffix = sanitizeIdSuffix(reactInstanceId)
  const ids = useMemo<RenderIds>(() => ({
    gradientLuminanceId: `red-above-loc-grad-lum-${idSuffix}`,
    gradientSurfaceId: `red-above-loc-grad-surface-${idSuffix}`,
    maskId: `red-above-loc-mask-${idSuffix}`,
    continuousNoiseFilterId: `red-above-loc-cont-noise-${idSuffix}`,
    bandHighlightFeatherFilterId: `red-above-loc-band-hl-feather-${idSuffix}`,
    bandHighlightFeatherMaskId: `red-above-loc-band-hl-feather-mask-${idSuffix}`,
  }), [idSuffix])
  const settings = useBackgroundPatternSettings()

  const pathRender = useMemo(
    () => createRedAboveLossOfControlPathRender({
      show,
      lossOfControlMinPoints,
      lossOfControlMaxPoints,
      effectiveCappedTimestamp,
      xForTimestamp,
      yForValue,
    }),
    [
      show,
      lossOfControlMinPoints,
      lossOfControlMaxPoints,
      effectiveCappedTimestamp,
      xForTimestamp,
      yForValue,
    ],
  )

  const hoveredRiskPercent = useMemo(() => {
    if (!hoveredOverlayPoint || !pathRender || !settings.enabled) return null
    const gradientOpacity = getCurveGradientOpacityAtPoint(
      hoveredOverlayPoint,
      pathRender.minLineScreenPoints,
      pathRender.maxLineScreenPoints,
      settings
    )
    if (gradientOpacity === null) return null
    return Math.round(gradientOpacity * 100)
  }, [hoveredOverlayPoint, pathRender, settings])

  const hoveredBandIndex = useMemo(() => {
    if (!hoveredOverlayPoint || !pathRender || !settings.enabled) return null
    return getHoveredBandIndex(
      hoveredOverlayPoint,
      pathRender.minLineScreenPoints,
      pathRender.maxLineScreenPoints,
      settings
    )
  }, [hoveredOverlayPoint, pathRender, settings])

  // Ignores band-boundary noise so the label doesn't jiggle within a band.
  const hoveredBandLabel = useMemo(() => {
    if (hoveredBandIndex === null || !pathRender) return null
    const leftEdgeX = pathRender.minLineScreenPoints[0].x
    const leftEdgeMinY = pathRender.minLineScreenPoints[0].y
    const leftEdgeMaxY = interpolateCurveYAtX(
      leftEdgeX, pathRender.maxLineScreenPoints
    ) ?? leftEdgeMinY
    const stripHeight = leftEdgeMinY - leftEdgeMaxY
    const bandLowerLevel = getBandLowerProgress(
      hoveredBandIndex,
      settings.densityBands,
      settings.bandThicknessMultiplier,
      settings.bandLowerProgressOverrides
    )
    const bandUpperLevel = getBandLowerProgress(
      hoveredBandIndex + 1,
      settings.densityBands,
      settings.bandThicknessMultiplier,
      settings.bandLowerProgressOverrides
    )
    const centerLevel = (bandLowerLevel + bandUpperLevel) / 2
    const centerY = stripHeight > 0
      ? leftEdgeMinY - centerLevel * stripHeight
      : leftEdgeMaxY
    // Band center-Y at the left edge is its representative compute value.
    const dangerLabel = getRiskLabelForComputeValue(valueForY(centerY))
    if (!dangerLabel) return null
    // Clamp the label below the chart top (the top band's center can sit above y=0).
    const labelTopClampY =
      BAND_DANGER_LABEL_FONT_SIZE_PX / 2 + BAND_DANGER_LABEL_TOP_PADDING_PX
    return {
      dangerLabel,
      leftEdgeCenter: {
        x: leftEdgeX,
        y: Math.max(centerY, labelTopClampY),
      },
    }
  }, [hoveredBandIndex, pathRender, settings, valueForY])

  // Hover-highlight clip for the non-dithered branch only; dithered
  // uses restrictToBandIndex, avoiding this expensive ~2000-vertex polygon.
  const hoveredBandPolygon = useMemo(() => {
    if (hoveredBandIndex === null || !pathRender) return null
    if (settings.ditherPattern !== 'none') return null
    return buildBandHighlightRender({
      bandIndex: hoveredBandIndex,
      settings,
      minLineScreenPoints: pathRender.minLineScreenPoints,
      maxLineScreenPoints: pathRender.maxLineScreenPoints,
    })
  }, [hoveredBandIndex, pathRender, settings])

  useEffect(() => {
    onRiskHoverChange?.(hoveredRiskPercent)
  }, [hoveredRiskPercent, onRiskHoverChange])

  // Publish the danger label to the above-globe layer (graph-local
  // coords, no measurement needed). Hidden while faded.
  useEffect(() => {
    setLabel(
      hoveredBandLabel
        ? {
            anchor: hoveredBandLabel.leftEdgeCenter,
            text: hoveredBandLabel.dangerLabel,
            opacity: faded ? 0 : 1,
          }
        : null,
    )
    return () => setLabel(null)
  }, [hoveredBandLabel, faded, setLabel])

  if (!pathRender) return null
  if (!settings.enabled) return null

  const noiseSource = settings.noiseSource
  const ditherPattern = settings.ditherPattern
  const overlayColor = settings.color
  const overlayStyle = {
    fill: overlayColor,
    opacity: settings.opacity,
    transition: 'fill 500ms ease',
  }
  const fadeGroupStyle: CSSProperties = {
    opacity: faded ? 0 : 1,
    position: 'relative',
    left: -4,
    transition: `opacity ${OVERLAY_FADE_TRANSITION_MS}ms ease-in-out`,
  }
  const fadeGroupPointerEvents = faded ? 'none' : undefined
  const isFlat = noiseSource === 'none' && ditherPattern === 'none'
  const isContinuousNoise = noiseSource !== 'none' && ditherPattern === 'none'
  const isDithered = ditherPattern !== 'none'
  // Hover-only: static pattern is a smooth gradient; densityBands
  // defines only the cursor-highlighted band's geometry.
  const densityBandsHoverOnly =
    settings.densityBandsHoverOnly && settings.densityBands >= 2
  // Static-pattern settings: band quantization off in hover-only mode.
  const mainPatternSettings: BackgroundPatternSettings = densityBandsHoverOnly
    ? { ...settings, densityBands: 0 }
    : settings

  const handleOverlayMouseMove = (event: MouseEvent<SVGPathElement>) => {
    const point = getSvgMousePoint(event)
    if (!point) return
    setHoveredOverlayPoint(point)
  }

  // Hovered band re-rendered in a darker/saturated shade, clipped to the band.
  const bandHighlightOverlayColor = adjustHexColorHsl(
    overlayColor,
    settings.bandHighlightBrightnessMultiplier,
    settings.bandHighlightSaturationMultiplier,
  )
  const bandHighlightDotThicknessAdjustment: DotThicknessAdjustment = {
    dotRadiusMultiplier: settings.bandHighlightDotRadiusMultiplier,
    dotRadiusAdditivePx: settings.bandHighlightDotRadiusAdditivePx,
  }
  // Feather sized as a fraction of average band thickness for consistency.
  const averageStripHeight = (() => {
    if (hoveredBandIndex === null) return 0
    let total = 0
    let count = 0
    for (const minCurvePoint of pathRender.minLineScreenPoints) {
      const maxYAtX = interpolateCurveYAtX(minCurvePoint.x, pathRender.maxLineScreenPoints)
      if (maxYAtX === null) continue
      total += Math.max(0, minCurvePoint.y - maxYAtX)
      count += 1
    }
    return count > 0 ? total / count : 0
  })()
  const bandHighlightFeatherStdDev = hoveredBandIndex !== null
    ? (averageStripHeight / Math.max(2, settings.densityBands))
      * settings.bandHighlightEdgeSoftness
    : 0
  // 6 stddevs ≈ 99.99% of the gaussian; padding so the blur isn't clipped.
  const bandHighlightFeatherRegionPadding = bandHighlightFeatherStdDev * 6
  const bandHighlightFeatherDefs = hoveredBandPolygon ? (
    <defs>
      <filter
        id={ids.bandHighlightFeatherFilterId}
        filterUnits="userSpaceOnUse"
        x={pathRender.bounds.x - bandHighlightFeatherRegionPadding}
        y={pathRender.bounds.y - bandHighlightFeatherRegionPadding}
        width={pathRender.bounds.width + bandHighlightFeatherRegionPadding * 2}
        height={pathRender.bounds.height + bandHighlightFeatherRegionPadding * 2}
      >
        <feGaussianBlur stdDeviation={bandHighlightFeatherStdDev} />
      </filter>
      <mask
        id={ids.bandHighlightFeatherMaskId}
        maskUnits="userSpaceOnUse"
        maskContentUnits="userSpaceOnUse"
        x={pathRender.bounds.x - bandHighlightFeatherRegionPadding}
        y={pathRender.bounds.y - bandHighlightFeatherRegionPadding}
        width={pathRender.bounds.width + bandHighlightFeatherRegionPadding * 2}
        height={pathRender.bounds.height + bandHighlightFeatherRegionPadding * 2}
      >
        <path
          d={hoveredBandPolygon.pathData}
          fill="white"
          filter={`url(#${ids.bandHighlightFeatherFilterId})`}
        />
      </mask>
    </defs>
  ) : null

  // Danger label isn't rendered inline here; it's published to the above-globe layer.

  if (isDithered) {
    return (
      <g style={fadeGroupStyle} pointerEvents={fadeGroupPointerEvents}>
        {!interactiveOnly && (
          <FinalDitherCanvas
            pathRender={pathRender}
            settings={mainPatternSettings}
            fillColor={overlayColor}
            capabilityLineScreenPoints={capabilityLineScreenPoints}
            additionalCapabilityLineScreenPointsList={additionalCapabilityLinePolylines}
            fadingCapabilityLinePolylines={fadingCapabilityLinePolylines}
            plotSpanX={plotSpanX}
          />
        )}
        {hoveredBandIndex !== null ? (
          // Tile-perfect highlight; full settings so banding is on
          // even in hover-only mode (mainPatternSettings.densityBands is 0).
          <FinalDitherCanvas
            pathRender={pathRender}
            settings={settings}
            fillColor={bandHighlightOverlayColor}
            capabilityLineScreenPoints={capabilityLineScreenPoints}
            additionalCapabilityLineScreenPointsList={additionalCapabilityLinePolylines}
            fadingCapabilityLinePolylines={fadingCapabilityLinePolylines}
            thicknessAdjustment={bandHighlightDotThicknessAdjustment}
            restrictToBandIndex={hoveredBandIndex}
            featherStdDev={bandHighlightFeatherStdDev}
            fadeInMs={BAND_HIGHLIGHT_FADE_MS}
            publishAboveGlobe
            plotSpanX={plotSpanX}
          />
        ) : null}
        <path
          d={pathRender.fillPathData}
          fill="transparent"
          pointerEvents="fill"
          onMouseMove={handleOverlayMouseMove}
          onMouseLeave={() => setHoveredOverlayPoint(null)}
        />
      </g>
    )
  }

  const gradientLuminanceDefs = renderGradientLuminanceDefs({
    topY: pathRender.bounds.y,
    settings: mainPatternSettings,
    gradientIdPrefix: ids.gradientLuminanceId,
    surfaceId: ids.gradientSurfaceId,
    minLineScreenPoints: pathRender.minLineScreenPoints,
    maxLineScreenPoints: pathRender.maxLineScreenPoints,
  })

  const continuousNoiseFilter = isContinuousNoise
    ? renderContinuousNoisePlusGradientFilter(
        noiseSource,
        ids.continuousNoiseFilterId,
        ids.gradientSurfaceId,
        pathRender.bounds,
        mainPatternSettings
      )
    : null

  let maskContent: ReactNode
  if (isFlat) {
    maskContent = <use href={`#${ids.gradientSurfaceId}`} />
  } else if (isContinuousNoise) {
    maskContent = renderMaskRect(pathRender.bounds, {
      fill: '#ffffff',
      filter: `url(#${ids.continuousNoiseFilterId})`,
    })
  } else {
    return null
  }

  return (
    <g style={fadeGroupStyle} pointerEvents={fadeGroupPointerEvents}>
      <defs>
        {gradientLuminanceDefs}
        {continuousNoiseFilter}
        <mask
          id={ids.maskId}
          maskUnits="userSpaceOnUse"
          maskContentUnits="userSpaceOnUse"
          x={pathRender.bounds.x}
          y={pathRender.bounds.y}
          width={pathRender.bounds.width}
          height={pathRender.bounds.height}
        >
          {maskContent}
        </mask>
      </defs>
      {bandHighlightFeatherDefs}
      <path
        d={pathRender.fillPathData}
        mask={`url(#${ids.maskId})`}
        style={overlayStyle}
        onMouseMove={handleOverlayMouseMove}
        onMouseLeave={() => setHoveredOverlayPoint(null)}
      />
      {hoveredBandPolygon ? (
        <g mask={`url(#${ids.bandHighlightFeatherMaskId})`}>
          <path
            d={pathRender.fillPathData}
            mask={`url(#${ids.maskId})`}
            style={{ fill: bandHighlightOverlayColor, opacity: settings.opacity }}
            pointerEvents="none"
          />
        </g>
      ) : null}
    </g>
  )
}
